import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChartPie, Folder, Briefcase, ChartLineUp, ChatCircle, Monitor, User, Gear } from '@phosphor-icons/react';
import { apiBase, authTokenStorageKey, themeStorageKey, themeOptions } from './utils/constants';
import { formatNumber, formatSigned, formatSignedPercent, formatPercent, formatCurrencyBreakdown, formatCompactDate, getPulseValueDigits } from './utils/format';
import { getMonthRange } from './utils/dateRange';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import { DesktopOnlyNotice } from './components/DesktopOnlyNotice';
import { DocumentsWorkspace } from './components/DocumentsWorkspace';
import { downloadCsv } from './utils/csv';
import {
  uniqueNonEmptyStrings,
  getExtractionFailureReasonLabel,
  buildFailedFileEntries,
  getFilePeriodLabel,
  getFileDateLabel,
  getDocumentDateValue,
  getDocumentPeriodParts,
  getFileKey,
  clampUnitValue,
  revokeObjectUrl
} from './utils/documents';
import {
  getLinkedEventTypeLabel,
  getLinkedEventSecondaryText,
  getLinkedEventNoteText,
  getLedgerTypeLabel,
  analyticsPositionFilters,
  assetPositionStatusKeys,
  getAssetPositionStatusLabel,
  getAssetPositionStatus,
  getPositionFilterLabel,
  getPositionScopedAssets,
  buildCurrencyUsdRateMap,
  mapFinancingInterestDetails,
  getScopedFinancingInterestMetrics
} from './utils/ledger';
import { parseTermSheetPage } from './utils/termSheet';
import { useI18n } from './i18n/useI18n';
import { LanguageToggle } from './i18n/LanguageToggle';
import { StructuredProductDashboard } from './components/StructuredProductDashboard';
import { ConfigurePanel } from './components/ConfigurePanel';
import { OverviewPanel } from './components/OverviewPanel';

function App() {
  const { t } = useI18n();
  const [authToken, setAuthToken] = useState(() => window.localStorage.getItem(authTokenStorageKey) || '');
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [securityStatus, setSecurityStatus] = useState({ encryption_enabled: false, encryption_available: false, unlocked: false, db_exists: false, db_encrypted: false });
  const [masterKeyInput, setMasterKeyInput] = useState('');
  const [masterKeyLoading, setMasterKeyLoading] = useState(false);
  const [securityChecking, setSecurityChecking] = useState(true);
  const [userManagement, setUserManagement] = useState({ users: [] });
  const [featureSettings, setFeatureSettings] = useState({ talker_enabled: false, preview_redaction_enabled: true, db_management_enabled: true });
  const [userForm, setUserForm] = useState({ username: '', password: '', role: 'user' });
  const [passwordResetDrafts, setPasswordResetDrafts] = useState({});
  const [userActionLoadingKey, setUserActionLoadingKey] = useState('');
  const isMobileViewport = useIsMobileViewport();
  const [mobileAnalyticsDetailOpen, setMobileAnalyticsDetailOpen] = useState(false);
  const [health, setHealth] = useState(null);
  const [dashboardData, setDashboardData] = useState({ assets: [], market_pulse: [], overview: null, period: null, period_assets: [] });
  const [talkerLearning, setTalkerLearning] = useState({ open_bad_case_count: 0, active_fewshot_count: 0, open_bad_cases: [], active_fewshots: [], semantic_candidates: [] });
  const [theme, setTheme] = useState(() => window.localStorage.getItem(themeStorageKey) || 'default');
  const [keyword, setKeyword] = useState('MARUBENI');
  const [assetFilter, setAssetFilter] = useState('');
  const [overviewPositionFilter, setOverviewPositionFilter] = useState('CURRENT');
  const [analyticsPositionFilter, setAnalyticsPositionFilter] = useState('CURRENT');
  const [workspacePositionFilter, setWorkspacePositionFilter] = useState('CURRENT');
  const [analyticsPage, setAnalyticsPage] = useState(1);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [linkedEventPage, setLinkedEventPage] = useState(1);
  const [overviewFilter, setOverviewFilter] = useState('');
  const [workspacePage, setWorkspacePage] = useState(1);
  const [workspaceSort, setWorkspaceSort] = useState({ key: 'security_name', direction: 'asc' });
  const [periodRange, setPeriodRange] = useState({ startDate: '', endDate: '' });
  const [monthPicker, setMonthPicker] = useState('');
  const [expandedYears, setExpandedYears] = useState({});
  const [expandedMonths, setExpandedMonths] = useState({});
  const [redactionStatusByFile, setRedactionStatusByFile] = useState({});
  const hasInitializedDashboardRangeEffect = useRef(false);
  const [ledger, setLedger] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('overview');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectionSummary, setSelectionSummary] = useState(null);
  const [extractionItems, setExtractionItems] = useState([]);
  const [extractionFailures, setExtractionFailures] = useState([]);
  const [documentExtractionSummary, setDocumentExtractionSummary] = useState(null);
  const [extractionTaskId, setExtractionTaskId] = useState(null);
  const [extractionTaskStatus, setExtractionTaskStatus] = useState(null);
  const extractionTaskPollRef = useRef(null);
  const [lifecycle, setLifecycle] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [pdfPreview, setPdfPreview] = useState({ url: '', title: '', page: null, open: false, documentId: null, fileName: '', pageImageUrl: '' });
  const [pdfPreviewScale, setPdfPreviewScale] = useState(1);
  const [pdfRedactionMode, setPdfRedactionMode] = useState(false);
  const [pdfRedactionPreviewOnly, setPdfRedactionPreviewOnly] = useState(false);
  const [pdfRedactionLoading, setPdfRedactionLoading] = useState(false);
  const [pdfRedactionSuggestions, setPdfRedactionSuggestions] = useState(null);
  const [pdfRedactionDrafts, setPdfRedactionDrafts] = useState([]);
  const [pdfRedactionLabel, setPdfRedactionLabel] = useState('name_address');
  const [pdfRedactionSavingKey, setPdfRedactionSavingKey] = useState('');
  const [pdfRedactionDrawingBox, setPdfRedactionDrawingBox] = useState(null);
  const [selectedAnalyticsPanel, setSelectedAnalyticsPanel] = useState('LINKED');
  const [selectedLinkedEventKey, setSelectedLinkedEventKey] = useState('ALL');
  const [manualLinkedAssets, setManualLinkedAssets] = useState({});
  const [savingLinkedReferenceNo, setSavingLinkedReferenceNo] = useState('');
  const [interestDetailDrawerOpen, setInterestDetailDrawerOpen] = useState(false);
  const [assetEditMode, setAssetEditMode] = useState(false);
  const [assetEditForm, setAssetEditForm] = useState({ asset_no: '', stock_ticker: '', security_name: '', currency: '', asset_type: '' });
  const [assetEditSaving, setAssetEditSaving] = useState(false);
  const [talkerMessages, setTalkerMessages] = useState([
    {
      role: 'assistant',
      content: t('talker.greeting'),
      sql: '',
      columns: [],
      rows: [],
      usedSql: false
    }
  ]);
  const [talkerInput, setTalkerInput] = useState('');
  const [talkerMode, setTalkerMode] = useState('fast');
  const [talkerLoading, setTalkerLoading] = useState(false);
  const [reviewActionLoadingKey, setReviewActionLoadingKey] = useState('');
  const [fewshotReviewPage, setFewshotReviewPage] = useState(1);
  const monthInputRef = useRef(null);
  const startDateInputRef = useRef(null);
  const endDateInputRef = useRef(null);
  const talkerTypewriterTimerRef = useRef(null);
  const talkerMessageListRef = useRef(null);
  const talkerFollowScrollRef = useRef(true);
  const previousTalkerMessageCountRef = useRef(talkerMessages.length);
  const talkerPendingScrollTargetRef = useRef('');
  const pdfRedactionCanvasRef = useRef(null);
  const pdfRedactionPointerStartRef = useRef(null);
  const [pdfRedactionNaturalSize, setPdfRedactionNaturalSize] = useState(null);
  const [pdfRedactionRenderRect, setPdfRedactionRenderRect] = useState(null);

  const apiFetch = async (path, options = {}) => {
    const headers = {
      ...(options.headers || {})
    };
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }
    const response = await fetch(`${apiBase}${path}`, {
      ...options,
      headers
    });
    if (response.status === 401) {
      setAuthToken('');
      setCurrentUser(null);
      window.localStorage.removeItem(authTokenStorageKey);
    }
    if (response.status === 503) {
      loadSecurityStatus();
    }
    return response;
  };

  const loadCurrentUser = async () => {
    if (!authToken) {
      setCurrentUser(null);
      setAuthChecking(false);
      return;
    }
    try {
      const response = await apiFetch('/auth/me');
      if (!response.ok) {
        setCurrentUser(null);
        return;
      }
      const data = await response.json();
      setCurrentUser(data.user || null);
    } catch (error) {
      setCurrentUser(null);
    } finally {
      setAuthChecking(false);
    }
  };

  const loadUsers = async () => {
    if (!currentUser || currentUser.role !== 'admin') {
      setUserManagement({ users: [] });
      return;
    }
    try {
      const response = await apiFetch('/users');
      if (!response.ok) {
        setUserManagement({ users: [] });
        return;
      }
      const data = await response.json();
      setUserManagement({ users: Array.isArray(data.users) ? data.users : [] });
    } catch (error) {
      setUserManagement({ users: [] });
    }
  };

  const loadFeatureSettings = async () => {
    if (!currentUser) {
      setFeatureSettings({ talker_enabled: false, preview_redaction_enabled: true, db_management_enabled: true });
      return;
    }
    try {
      const response = await apiFetch('/features');
      if (!response.ok) throw new Error('Unable to load feature settings');
      const data = await response.json();
      setFeatureSettings({ talker_enabled: Boolean(data.talker_enabled), preview_redaction_enabled: Boolean(data.preview_redaction_enabled), db_management_enabled: Boolean(data.db_management_enabled) });
    } catch (error) {
      setFeatureSettings({ talker_enabled: false, preview_redaction_enabled: true, db_management_enabled: true });
    }
  };

  const updateTalkerFeature = async (enabled) => {
    try {
      setUserActionLoadingKey('talker-feature');
      const response = await apiFetch('/users/features/talker', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || t('configure.features.talkerUpdateFailed'));
      setFeatureSettings({ talker_enabled: Boolean(data.talker_enabled), preview_redaction_enabled: Boolean(data.preview_redaction_enabled), db_management_enabled: Boolean(data.db_management_enabled) });
      if (!data.talker_enabled && activeView === 'talker') setActiveView('overview');
      setMessage(t(data.talker_enabled ? 'configure.features.talkerEnabled' : 'configure.features.talkerDisabled'));
    } catch (error) {
      setMessage(error.message || t('configure.features.talkerUpdateFailed'));
    } finally {
      setUserActionLoadingKey('');
    }
  };

  const updatePreviewRedactionFeature = async (enabled) => {
    try {
      setUserActionLoadingKey('preview-redaction-feature');
      const response = await apiFetch('/users/features/preview-redaction', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || t('configure.features.previewRedactionUpdateFailed'));
      setFeatureSettings({ talker_enabled: Boolean(data.talker_enabled), preview_redaction_enabled: Boolean(data.preview_redaction_enabled), db_management_enabled: Boolean(data.db_management_enabled) });
      setMessage(t(data.preview_redaction_enabled ? 'configure.features.previewRedactionEnabled' : 'configure.features.previewRedactionDisabled'));
    } catch (error) {
      setMessage(error.message || t('configure.features.previewRedactionUpdateFailed'));
    } finally {
      setUserActionLoadingKey('');
    }
  };

  const updateDbManagementFeature = async (enabled) => {
    try {
      setUserActionLoadingKey('db-management-feature');
      const response = await apiFetch('/users/features/db-management', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || t('configure.features.dbManagementUpdateFailed'));
      setFeatureSettings({ talker_enabled: Boolean(data.talker_enabled), preview_redaction_enabled: Boolean(data.preview_redaction_enabled), db_management_enabled: Boolean(data.db_management_enabled) });
      setMessage(t(data.db_management_enabled ? 'configure.features.dbManagementEnabled' : 'configure.features.dbManagementDisabled'));
    } catch (error) {
      setMessage(error.message || t('configure.features.dbManagementUpdateFailed'));
    } finally {
      setUserActionLoadingKey('');
    }
  };

  const submitLogin = async () => {
    const username = String(loginForm.username || '').trim();
    const password = String(loginForm.password || '');
    if (!username || !password) {
      setMessage(t('auth.enterCredentials'));
      return;
    }
    try {
      setLoginLoading(true);
      const response = await fetch(`${apiBase}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || t('auth.loginFailed'));
      }
      setAuthToken(data.token || '');
      window.localStorage.setItem(authTokenStorageKey, data.token || '');
      setCurrentUser(data.user || null);
      setLoginForm((current) => ({ ...current, password: '' }));
      setMessage(t('auth.welcomeBack', { name: data.user?.username || username }));
    } catch (error) {
      setMessage(error.message || t('auth.loginFailed'));
    } finally {
      setLoginLoading(false);
      setAuthChecking(false);
    }
  };

  const logout = async () => {
    try {
      if (authToken) {
        await apiFetch('/auth/logout', { method: 'POST' });
      }
    } catch (error) {
    } finally {
      setAuthToken('');
      setCurrentUser(null);
      setUserManagement({ users: [] });
      setSecurityStatus({ encryption_enabled: false, encryption_available: false, unlocked: false, db_exists: false, db_encrypted: false });
      window.localStorage.removeItem(authTokenStorageKey);
      setActiveView('overview');
    }
  };

  const loadSecurityStatus = async () => {
    try {
      const response = await fetch(`${apiBase}/security/status`);
      if (response.ok) {
        const data = await response.json();
        setSecurityStatus(data);
      }
    } catch (error) {
    } finally {
      setSecurityChecking(false);
    }
  };

  const submitMasterKey = async () => {
    const key = String(masterKeyInput || '').trim();
    if (!key) {
      setMessage(t('security.enterKey'));
      return;
    }
    try {
      setMasterKeyLoading(true);
      const response = await apiFetch('/security/unlock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ master_key: key })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || t('security.unlockFailed'));
      }
      setSecurityStatus((prev) => ({ ...prev, unlocked: true }));
      setMasterKeyInput('');
      setMessage(data.migrated ? t('security.migrated') : t('security.unlocked'));
      loadSecurityStatus();
    } catch (error) {
      setMessage(error.message || t('security.unlockFailed'));
    } finally {
      setMasterKeyLoading(false);
    }
  };

  const submitCreateUser = async () => {
    const username = String(userForm.username || '').trim();
    const password = String(userForm.password || '');
    if (!username || !password) {
      setMessage(t('configure.users.enterNewCredentials'));
      return;
    }
    try {
      setUserActionLoadingKey('create-user');
      const response = await apiFetch('/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, role: userForm.role || 'user' })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || t('configure.users.createFailed'));
      }
      setUserForm({ username: '', password: '', role: 'user' });
      setMessage(data.message || t('configure.users.createSuccess'));
      await loadUsers();
    } catch (error) {
      setMessage(error.message || t('configure.users.createFailed'));
    } finally {
      setUserActionLoadingKey('');
    }
  };

  const submitResetPassword = async (userId) => {
    const newPassword = String(passwordResetDrafts[userId] || '');
    if (!newPassword) {
      setMessage(t('configure.users.enterNewPassword'));
      return;
    }
    try {
      setUserActionLoadingKey(`reset-${userId}`);
      const response = await apiFetch(`/users/${userId}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: newPassword })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || t('configure.users.resetFailed'));
      }
      setPasswordResetDrafts((current) => ({ ...current, [userId]: '' }));
      setMessage(data.message || t('configure.users.resetSuccess'));
      await loadUsers();
    } catch (error) {
      setMessage(error.message || t('configure.users.resetFailed'));
    } finally {
      setUserActionLoadingKey('');
    }
  };

  const updateManualLinkedAsset = (referenceNo, assetNo) => {
    setManualLinkedAssets((current) => ({
      ...current,
      [referenceNo]: assetNo
    }));
  };

  const saveFinancingRelink = async (referenceNo) => {
    const targetAssetNo = String(manualLinkedAssets[referenceNo] || '').trim();
    if (!referenceNo || !targetAssetNo) {
      setMessage(t('linked.selectTarget'));
      return;
    }
    try {
      setSavingLinkedReferenceNo(referenceNo);
      const response = await apiFetch('/financing/relink', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reference_no: referenceNo, asset_no: targetAssetNo })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || t('linked.saveFailed'));
      }
      await loadDashboard();
      await loadLifecycle();
      setMessage(t('linked.saveSuccess', { ref: referenceNo, asset: targetAssetNo }));
    } catch (error) {
      setMessage(error.message || t('linked.saveFailed'));
    } finally {
      setSavingLinkedReferenceNo('');
    }
  };

  const fewshotReviewPageSize = 5;
  const fewshotReviewTotalPages = Math.max(1, Math.ceil((talkerLearning.active_fewshots || []).length / fewshotReviewPageSize));
  const safeFewshotReviewPage = Math.min(fewshotReviewPage, fewshotReviewTotalPages);
  const pagedFewshotReviewItems = (talkerLearning.active_fewshots || []).slice((safeFewshotReviewPage - 1) * fewshotReviewPageSize, safeFewshotReviewPage * fewshotReviewPageSize);

  const submitLearningAction = async (path, loadingKey, successMessage) => {
    try {
      setReviewActionLoadingKey(loadingKey);
      const response = await apiFetch(path, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || t('linked.reviewFailed'));
      }
      setMessage(successMessage || data.message || t('linked.reviewSuccess'));
      await loadTalkerLearning();
    } catch (error) {
      setMessage(error.message || t('linked.reviewFailed'));
    } finally {
      setReviewActionLoadingKey('');
    }
  };

  const selectAsset = async (assetKeyword) => {
    const nextKeyword = String(assetKeyword || '').trim();
    if (!nextKeyword) {
      return;
    }
    setKeyword(nextKeyword);
    if (isMobileViewport) {
      setMobileAnalyticsDetailOpen(true);
    }
    await loadLedger(nextKeyword);
    await loadLifecycle(nextKeyword);
  };

  const loadHealth = async () => {
    try {
      const response = await apiFetch('/health');
      const data = await response.json();
      setHealth(data);
    } catch (error) {
      setHealth({ status: 'offline', model: t('misc.healthOffline'), api_url: apiBase });
    }
  };

  const loadTalkerLearning = async () => {
    try {
      const response = await apiFetch('/talker/learning');
      if (!response.ok) {
        setTalkerLearning({ open_bad_case_count: 0, active_fewshot_count: 0, open_bad_cases: [], active_fewshots: [], semantic_candidates: [] });
        return;
      }
      const data = await response.json();
      setTalkerLearning({
        open_bad_case_count: Number(data.open_bad_case_count || 0),
        active_fewshot_count: Number(data.active_fewshot_count || 0),
        open_bad_cases: Array.isArray(data.open_bad_cases) ? data.open_bad_cases : [],
        active_fewshots: Array.isArray(data.active_fewshots) ? data.active_fewshots : [],
        semantic_candidates: Array.isArray(data.semantic_candidates) ? data.semantic_candidates : []
      });
    } catch (error) {
      setTalkerLearning({ open_bad_case_count: 0, active_fewshot_count: 0, open_bad_cases: [], active_fewshots: [], semantic_candidates: [] });
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await apiFetch('/documents');
      if (!response.ok) {
        setDocuments([]);
        return;
      }
      const data = await response.json();
      setDocuments(data.documents || []);
    } catch (error) {
      setDocuments([]);
    }
  };

  const loadLifecycle = async (assetKeyword = keyword) => {
    const normalizedKeyword = String(assetKeyword || '').trim();
    if (!normalizedKeyword) {
      setLifecycle(null);
      return;
    }
    try {
      const response = await apiFetch(`/assets/${encodeURIComponent(normalizedKeyword)}/lifecycle`);
      if (!response.ok) {
        setLifecycle(null);
        return;
      }
      const data = await response.json();
      setLifecycle(data);
    } catch (error) {
      setLifecycle(null);
    }
  };

  const loadDashboard = async () => {
    try {
      const query = new URLSearchParams();
      if (periodRange.startDate) {
        query.set('start_date', periodRange.startDate);
      }
      if (periodRange.endDate) {
        query.set('end_date', periodRange.endDate);
      }
      const response = await apiFetch(`/dashboard${query.toString() ? `?${query.toString()}` : ''}`);
      const data = await response.json();
      setDashboardData({
        assets: data.assets || [],
        market_pulse: data.market_pulse || [],
        overview: data.overview || null,
        period: data.period || null,
        period_assets: data.period_assets || []
      });
    } catch (error) {
      setDashboardData({ assets: [], market_pulse: [], overview: null, period: null, period_assets: [] });
    }
  };

  const loadDashboardPeriod = async () => {
    try {
      const query = new URLSearchParams();
      if (periodRange.startDate) {
        query.set('start_date', periodRange.startDate);
      }
      if (periodRange.endDate) {
        query.set('end_date', periodRange.endDate);
      }
      const response = await apiFetch(`/dashboard/period${query.toString() ? `?${query.toString()}` : ''}`);
      if (!response.ok) {
        throw new Error('period request failed');
      }
      const data = await response.json();
      setDashboardData((current) => ({
        ...current,
        period: data.period || null,
        period_assets: data.period_assets || []
      }));
    } catch (error) {
      setDashboardData((current) => ({ ...current, period: null, period_assets: [] }));
    }
  };

  const loadLedger = async (assetKeyword = keyword) => {
    const normalizedKeyword = String(assetKeyword || '').trim();
    if (!normalizedKeyword) {
      setLedger([]);
      setMessage(t('misc.enterAssetKeyword'));
      return;
    }
    try {
      const response = await apiFetch(`/assets/${encodeURIComponent(normalizedKeyword)}/ledger`);
      if (!response.ok) {
        setLedger([]);
        setMessage(t('misc.assetNotFound'));
        return;
      }
      const data = await response.json();
      setLedger(data.items || []);
      setMessage('');
    } catch (error) {
      setLedger([]);
      setMessage(t('misc.backendError'));
    }
  };

  const submitTalkerMessage = async () => {
    const normalizedMessage = String(talkerInput || '').trim();
    if (!normalizedMessage || talkerLoading) {
      return;
    }
    const userMessage = { role: 'user', content: normalizedMessage };
    talkerPendingScrollTargetRef.current = 'user';
    setTalkerMessages((current) => [...current, userMessage]);
    setTalkerInput('');
    setTalkerLoading(true);
    try {
      const response = await apiFetch('/talker/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: normalizedMessage,
          mode: talkerMode,
          history: talkerMessages.slice(-8),
          start_date: periodRange.startDate,
          end_date: periodRange.endDate
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || t('talker.requestFailed'));
      }
      setTalkerMessages((current) => [...current, {
        role: 'assistant',
        content: data.answer || t('talker.noAnswer'),
        displayContent: '',
        sql: data.sql || '',
        columns: data.columns || [],
        rows: data.rows || [],
        usedSql: Boolean(data.used_sql)
      }]);
    } catch (error) {
      setTalkerMessages((current) => [...current, { role: 'assistant', content: t('talker.requestError', { error: error.message || '' }), displayContent: '', sql: '', columns: [], rows: [], usedSql: false }]);
    } finally {
      setTalkerLoading(false);
    }
  };

  const openNativePicker = (inputRef) => {
    const element = inputRef?.current;
    if (!element) {
      return;
    }
    if (typeof element.showPicker === 'function') {
      try {
        element.showPicker();
      } catch (error) {
        element.focus();
      }
    }
  };

  const handleTalkerInputKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitTalkerMessage();
    }
  };

  useEffect(() => {
    const targetIndex = talkerMessages.findIndex((item) => item.role === 'assistant' && typeof item.content === 'string' && (item.displayContent ?? '') !== item.content);
    if (targetIndex === -1) {
      if (talkerTypewriterTimerRef.current) {
        window.clearInterval(talkerTypewriterTimerRef.current);
        talkerTypewriterTimerRef.current = null;
      }
      return undefined;
    }
    if (talkerTypewriterTimerRef.current) {
      window.clearInterval(talkerTypewriterTimerRef.current);
    }
    talkerTypewriterTimerRef.current = window.setInterval(() => {
      setTalkerMessages((current) => {
        const next = [...current];
        const item = next[targetIndex];
        if (!item || item.role !== 'assistant') {
          return current;
        }
        const fullText = String(item.content || '');
        const currentDisplay = String(item.displayContent || '');
        if (currentDisplay.length >= fullText.length) {
          return current;
        }
        next[targetIndex] = {
          ...item,
          displayContent: fullText.slice(0, currentDisplay.length + 1)
        };
        return next;
      });
      // Auto-scroll during typewriter only if user is near bottom
      const container = talkerMessageListRef.current;
      if (container && talkerFollowScrollRef.current) {
        const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        if (distanceToBottom <= 48) {
          container.scrollTop = container.scrollHeight;
        }
      }
    }, 18);
    return () => {
      if (talkerTypewriterTimerRef.current) {
        window.clearInterval(talkerTypewriterTimerRef.current);
        talkerTypewriterTimerRef.current = null;
      }
    };
  }, [talkerMessages]);

  useEffect(() => {
    const container = talkerMessageListRef.current;
    if (!container) {
      return;
    }
    const isNearBottom = () => {
      const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      return distanceToBottom <= 48;
    };
    const syncScrollFollowState = () => {
      talkerFollowScrollRef.current = isNearBottom();
    };
    syncScrollFollowState();
    container.addEventListener('scroll', syncScrollFollowState, { passive: true });
    return () => {
      container.removeEventListener('scroll', syncScrollFollowState);
    };
  }, []);

  useEffect(() => {
    const container = talkerMessageListRef.current;
    if (!container) {
      previousTalkerMessageCountRef.current = talkerMessages.length;
      return;
    }
    const previousCount = previousTalkerMessageCountRef.current;
    const currentCount = talkerMessages.length;
    const latestMessage = currentCount ? talkerMessages[currentCount - 1] : null;
    const pendingScrollTarget = talkerPendingScrollTargetRef.current;
    if (pendingScrollTarget === 'user' && latestMessage?.role === 'user') {
      container.scrollTop = container.scrollHeight;
      talkerPendingScrollTargetRef.current = '';
      talkerFollowScrollRef.current = true;
      previousTalkerMessageCountRef.current = currentCount;
      return;
    }
    const isNewAssistantMessage = currentCount > previousCount && latestMessage?.role === 'assistant';
    if (isNewAssistantMessage && talkerFollowScrollRef.current) {
      container.scrollTop = container.scrollHeight;
    }
    previousTalkerMessageCountRef.current = currentCount;
  }, [talkerMessages.length]);

  const triggerExtraction = async () => {
      setLoading(true);
      setMessage(t('docs.submitting'));
      setExtractionFailures([]);
      setDocumentExtractionSummary(null);
      try {
        if (!selectedFiles.length) {
          throw new Error(t('docs.selectFirst'));
        }
        const formData = new FormData();
        selectedFiles.forEach((file) => {
          formData.append('files', file);
        });
        const response = await apiFetch('/async-extract/upload', {
          method: 'POST',
          body: formData
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.detail || t('docs.submitFailed'));
        }
        setExtractionTaskId(data.task_id);
        setMessage(t('docs.queued'));
        setLoading(false);
      } catch (error) {
        setMessage(error.message);
        setLoading(false);
      }
    };

    // Poll task status when extractionTaskId changes
    useEffect(() => {
      if (!extractionTaskId) { return; }
      const pollInterval = 2000;
      let pollCount = 0;
      const poll = () => {
        apiFetch('/async-extract/tasks/' + extractionTaskId).then(async (response) => {
          if (!response.ok) {
            setExtractionTaskId(null);
            setMessage(t('docs.statusFailed'));
            return;
          }
          const taskData = await response.json();
          setExtractionTaskStatus(taskData);
          setMessage(t('docs.polling', { done: taskData.processed_pages + taskData.failed_pages, total: taskData.estimated_total_pages }));
          if (taskData.status === 'completed') {
            setExtractionTaskId(null);
            setExtractionTaskStatus(null);
            setMessage('');
            if (taskData.extraction) {
              const extraction = taskData.extraction;
              setExtractionItems(extraction.items || []);
              setExtractionFailures(extraction.failures || []);
              const successfulFileNames = uniqueNonEmptyStrings((extraction.items || []).map(function(i) { return i.source_file; }));
              const failedFileEntries = buildFailedFileEntries(extraction.failures || [], t);
              const duplicateFileEntries = buildFailedFileEntries(extraction.duplicates || [], t);
              setDocumentExtractionSummary({
                processedFiles: Number(extraction.processed_files || 0),
                processedPages: Number(extraction.processed_pages || 0),
                failedFiles: Number(extraction.failed_files || 0),
                failedPages: Number(extraction.failed_pages || 0),
                successfulFileNames: successfulFileNames,
                failedFileEntries: failedFileEntries,
                duplicateFiles: Number(extraction.duplicate_files || 0),
                duplicatePages: Number(extraction.duplicate_pages || 0),
                duplicateFileEntries: duplicateFileEntries
              });
              loadDashboard();
              loadDocuments();
            }
          } else if (taskData.status === 'failed') {
            setExtractionTaskId(null);
            setExtractionTaskStatus(null);
            setMessage(t('docs.parseFailed', { error: taskData.error_message || '' }));
          }
        }).catch(function() {
          pollCount++;
          if (pollCount > 10) {
            setExtractionTaskId(null);
            setMessage(t('docs.pollTimeout'));
          }
        });
      };
      poll();
      const intervalId = setInterval(poll, pollInterval);
      return function() { clearInterval(intervalId); };
    }, [extractionTaskId]);

  const updateFileSummary = async (files) => {
    const dedupedFiles = files.filter((file, index, list) => index === list.findIndex((candidate) => getFileKey(candidate) === getFileKey(file)));
    setSelectedFiles(dedupedFiles);
    setExtractionFailures([]);
    setDocumentExtractionSummary(null);
    if (!dedupedFiles.length) {
      setSelectionSummary(null);
      return;
    }
    const formData = new FormData();
    dedupedFiles.forEach((file) => formData.append('files', file));
    try {
      const response = await apiFetch('/files/summary', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      setSelectionSummary(data);
      setMessage(t('docs.selected', { count: data.pdf_files, dedup: dedupedFiles.length !== files.length ? t('docs.dedup') : '' }));
    } catch (error) {
      setSelectionSummary({ total_files: dedupedFiles.length, pdf_files: dedupedFiles.length, filenames: dedupedFiles.slice(0, 10).map((file) => file.name) });
    }
  };

  const handleFileChange = async (event) => {
    const incomingFiles = Array.from(event.target.files || []).filter((file) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'));
    await updateFileSummary(incomingFiles);
  };

  const removeSelectedFile = (targetName) => {
    const nextFiles = selectedFiles.filter((file) => file.name !== targetName);
    setSelectedFiles(nextFiles);
    if (pdfPreview.url.startsWith('blob:') && pdfPreview.title === targetName) {
      revokeObjectUrl(pdfPreview.url);
      resetPdfRedactionState('');
      setPdfPreview({ url: '', title: '', page: null, open: false, documentId: null, fileName: '', pageImageUrl: '' });
    }
    if (!nextFiles.length) {
      setSelectionSummary(null);
      return;
    }
    setSelectionSummary({
      total_files: nextFiles.length,
      pdf_files: nextFiles.length,
      filenames: nextFiles.slice(0, 20).map((file) => file.name)
    });
  };

  const resetPdfRedactionState = (nextPageImageUrl = '') => {
    if (pdfPreview.pageImageUrl && pdfPreview.pageImageUrl !== nextPageImageUrl) {
      revokeObjectUrl(pdfPreview.pageImageUrl);
    }
    setPdfRedactionMode(false);
    setPdfRedactionPreviewOnly(false);
    setPdfRedactionLoading(false);
    setPdfRedactionSuggestions(null);
    setPdfRedactionDrafts([]);
    setPdfRedactionSavingKey('');
    setPdfRedactionDrawingBox(null);
    setPdfRedactionNaturalSize(null);
    setPdfRedactionRenderRect(null);
    pdfRedactionPointerStartRef.current = null;
  };

  const previewSelectedFile = (file) => {
    revokeObjectUrl(pdfPreview.url);
    const objectUrl = URL.createObjectURL(file);
    resetPdfRedactionState('');
    setPdfPreviewScale(1);
    setPdfPreview({ url: objectUrl, title: file.name, page: null, open: true, documentId: null, fileName: file.name, pageImageUrl: '' });
  };

  const previewStoredDocument = async (documentId, title = null, page = null) => {
    revokeObjectUrl(pdfPreview.url);
    try {
      const response = await apiFetch(`/documents/${documentId}/file`);
      if (!response.ok) {
        let detail = t('pdf.previewFailed');
        throw new Error(detail);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      resetPdfRedactionState('');
      setPdfPreviewScale(1);
      const effectiveTitle = title || t('pdf.preview');
      setPdfPreview({
        url: objectUrl,
        title: effectiveTitle,
        page,
        open: true,
        documentId,
        fileName: effectiveTitle,
        pageImageUrl: ''
      });
      loadRedactionPreviewOverlay(documentId, effectiveTitle, Number(page || 1));
    } catch (error) {
      setMessage(error.message || t('pdf.previewFailed'));
      setPdfPreview({ url: '', title: '', page: null, open: false, documentId: null, fileName: '', pageImageUrl: '' });
    }
  };

  const loadRedactionPreviewOverlay = async (documentId, fileName, pageNumber = 1) => {
    try {
      const [imageResponse, suggestionResponse] = await Promise.all([
        apiFetch(`/documents/${encodeURIComponent(documentId)}/pages/${encodeURIComponent(pageNumber)}/image`),
        apiFetch(`/redaction-rules/suggestions?file_name=${encodeURIComponent(fileName)}`)
      ]);
      if (!imageResponse.ok || !suggestionResponse.ok) {
        return;
      }
      const imageBlob = await imageResponse.blob();
      const imageUrl = URL.createObjectURL(imageBlob);
      const suggestionData = await suggestionResponse.json();
      resetPdfRedactionState(imageUrl);
      setPdfPreview((current) => ({ ...current, pageImageUrl: imageUrl, page: pageNumber }));
      setPdfRedactionSuggestions(suggestionData);
      setPdfRedactionPreviewOnly(true);
    } catch (error) {
      // silently skip overlay if page image unavailable
    }
  };

  const changePreviewPage = (delta) => {
    if (!pdfPreview.documentId || pdfRedactionMode) {
      return;
    }
    const currentPage = Number(pdfPreview.page || 1);
    const nextPage = Math.max(1, currentPage + delta);
    if (nextPage === currentPage) {
      return;
    }
    setPdfPreviewScale(1);
    loadRedactionPreviewOverlay(pdfPreview.documentId, pdfPreview.fileName || pdfPreview.title || '', nextPage);
  };

  const pdfDragScrollRef = useRef(null);

  const handlePdfDragScrollDown = (event) => {
    const container = event.currentTarget;
    if (!container) return;
    pdfDragScrollRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
      moved: false,
    };
    container.style.cursor = 'grabbing';
  };

  const handlePdfDragScrollMove = (event) => {
    const state = pdfDragScrollRef.current;
    if (!state) return;
    const container = event.currentTarget;
    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      state.moved = true;
    }
    container.scrollLeft = state.scrollLeft - dx;
    container.scrollTop = state.scrollTop - dy;
  };

  const handlePdfDragScrollUp = () => {
    const container = pdfRedactionCanvasRef.current;
    if (container) {
      container.style.cursor = '';
    }
    pdfDragScrollRef.current = null;
  };

  const handlePdfDragScrollLeave = () => {
    if (pdfDragScrollRef.current) {
      const container = pdfRedactionCanvasRef.current;
      if (container) {
        container.style.cursor = '';
      }
      pdfDragScrollRef.current = null;
    }
  };

  const openPdfRedactionMode = async () => {
    if (!pdfPreview.documentId) {
      setMessage(t('pdf.onlyArchived'));
      return;
    }
    await enterPdfRedactionMode(pdfPreview.documentId, pdfPreview.fileName || pdfPreview.title || '', Number(pdfPreview.page || 1));
  };

  const enterPdfRedactionMode = async (documentId, fileName, pageNumber = 1) => {
    setPdfRedactionLoading(true);
    try {
      const [imageResponse, suggestionResponse] = await Promise.all([
        apiFetch(`/documents/${encodeURIComponent(documentId)}/pages/${encodeURIComponent(pageNumber)}/image`),
        apiFetch(`/redaction-rules/suggestions?file_name=${encodeURIComponent(fileName)}`)
      ]);
      if (!imageResponse.ok) {
        let detail = t('pdf.pagePreviewFailed');
        try {
          const data = await imageResponse.json();
          detail = data.detail || detail;
        } catch (error) {
          detail = imageResponse.status === 401 ? t('pdf.notLoggedIn') : detail;
        }
        throw new Error(detail);
      }
      if (!suggestionResponse.ok) {
        let detail = t('pdf.redactionLoadFailed');
        try {
          const data = await suggestionResponse.json();
          detail = data.detail || detail;
        } catch (error) {
          detail = suggestionResponse.status === 401 ? t('pdf.notLoggedIn') : detail;
        }
        throw new Error(detail);
      }
      const imageBlob = await imageResponse.blob();
      const imageUrl = URL.createObjectURL(imageBlob);
      const suggestionData = await suggestionResponse.json();
      resetPdfRedactionState(imageUrl);
      setPdfPreview((current) => ({ ...current, pageImageUrl: imageUrl }));
      setPdfRedactionSuggestions(suggestionData);
      setPdfRedactionPreviewOnly(false);
      setPdfRedactionMode(true);
    } catch (error) {
      setMessage(error.message || t('pdf.redactionAnnotateFailed'));
    } finally {
      setPdfRedactionLoading(false);
    }
  };

  const closePdfRedactionMode = () => {
    setPdfRedactionMode(false);
    setPdfRedactionPreviewOnly(true);
    setPdfRedactionDrafts([]);
    setPdfRedactionDrawingBox(null);
    setPdfRedactionSavingKey('');
    pdfRedactionPointerStartRef.current = null;
  };

  const refreshPdfRedactionSuggestions = async () => {
    const response = await apiFetch(`/redaction-rules/suggestions?file_name=${encodeURIComponent(pdfPreview.fileName || pdfPreview.title || '')}`);
    if (!response.ok) {
      let detail = t('pdf.redactionLoadFailed');
      try {
        const data = await response.json();
        detail = data.detail || detail;
      } catch (error) {
        detail = response.status === 401 ? t('pdf.notLoggedIn') : detail;
      }
      throw new Error(detail);
    }
    const data = await response.json();
    setPdfRedactionSuggestions(data);
    return data;
  };

  const computeContainRect = (containerWidth, containerHeight, naturalWidth, naturalHeight) => {
    if (!containerWidth || !containerHeight || !naturalWidth || !naturalHeight) {
      return null;
    }
    const containerRatio = containerWidth / containerHeight;
    const imageRatio = naturalWidth / naturalHeight;
    let renderWidth;
    let renderHeight;
    if (imageRatio > containerRatio) {
      renderWidth = containerWidth;
      renderHeight = containerWidth / imageRatio;
    } else {
      renderHeight = containerHeight;
      renderWidth = containerHeight * imageRatio;
    }
    return {
      left: (containerWidth - renderWidth) / 2,
      top: (containerHeight - renderHeight) / 2,
      width: renderWidth,
      height: renderHeight
    };
  };

  useEffect(() => {
    const container = pdfRedactionCanvasRef.current;
    if (!container || !pdfRedactionNaturalSize || !pdfPreview.pageImageUrl) {
      setPdfRedactionRenderRect(null);
      return undefined;
    }
    const updateRect = () => {
      const rect = container.getBoundingClientRect();
      setPdfRedactionRenderRect(
        computeContainRect(rect.width, rect.height, pdfRedactionNaturalSize.width, pdfRedactionNaturalSize.height)
      );
    };
    updateRect();
    const observer = new ResizeObserver(updateRect);
    observer.observe(container);
    return () => observer.disconnect();
  }, [pdfRedactionNaturalSize, pdfPreview.pageImageUrl, pdfRedactionMode, pdfRedactionPreviewOnly]);

  const handlePdfRedactionImageLoad = (event) => {
    setPdfRedactionNaturalSize({ width: event.target.naturalWidth, height: event.target.naturalHeight });
  };

  const getPdfRedactionPointer = (event) => {
    const container = pdfRedactionCanvasRef.current;
    if (!container) {
      return null;
    }
    const containerRect = container.getBoundingClientRect();
    const renderRect = pdfRedactionRenderRect || { left: 0, top: 0, width: containerRect.width, height: containerRect.height };
    if (!renderRect.width || !renderRect.height) {
      return null;
    }
    return {
      x: clampUnitValue((event.clientX - containerRect.left - renderRect.left) / renderRect.width),
      y: clampUnitValue((event.clientY - containerRect.top - renderRect.top) / renderRect.height)
    };
  };

  const handlePdfRedactionPointerDown = (event) => {
    if (!pdfRedactionMode || !pdfPreview.pageImageUrl) {
      return;
    }
    const point = getPdfRedactionPointer(event);
    if (!point) {
      return;
    }
    pdfRedactionPointerStartRef.current = point;
    setPdfRedactionDrawingBox({ x: point.x, y: point.y, w: 0, h: 0, label: pdfRedactionLabel });
  };

  const handlePdfRedactionPointerMove = (event) => {
    if (!pdfRedactionPointerStartRef.current) {
      return;
    }
    const point = getPdfRedactionPointer(event);
    if (!point) {
      return;
    }
    const startPoint = pdfRedactionPointerStartRef.current;
    const left = Math.min(startPoint.x, point.x);
    const top = Math.min(startPoint.y, point.y);
    const width = Math.abs(point.x - startPoint.x);
    const height = Math.abs(point.y - startPoint.y);
    setPdfRedactionDrawingBox({ x: left, y: top, w: width, h: height, label: pdfRedactionLabel });
  };

  const finishPdfRedactionPointer = () => {
    if (!pdfRedactionPointerStartRef.current || !pdfRedactionDrawingBox) {
      pdfRedactionPointerStartRef.current = null;
      setPdfRedactionDrawingBox(null);
      return;
    }
    const completedBox = pdfRedactionDrawingBox;
    pdfRedactionPointerStartRef.current = null;
    setPdfRedactionDrawingBox(null);
    if (completedBox.w < 0.01 || completedBox.h < 0.01) {
      return;
    }
    setPdfRedactionDrafts((current) => [
      ...current,
      {
        id: `${Date.now()}-${current.length + 1}`,
        page: Number(pdfPreview.page || 1),
        x: completedBox.x,
        y: completedBox.y,
        w: completedBox.w,
        h: completedBox.h,
        label: completedBox.label || pdfRedactionLabel
      }
    ]);
  };

  const savePdfRedactionDraft = async (draft) => {
    const savingKey = String(draft?.id || 'draft');
    setPdfRedactionSavingKey(savingKey);
    try {
      const response = await apiFetch('/redaction-rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          file_name: pdfPreview.fileName || pdfPreview.title || '',
          page: draft.page || Number(pdfPreview.page || 1),
          x: draft.x,
          y: draft.y,
          w: draft.w,
          h: draft.h,
          label: draft.label || pdfRedactionLabel
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || t('pdf.redactionSaveFailed'));
      }
      await refreshPdfRedactionSuggestions();
      setPdfRedactionDrafts((current) => current.filter((item) => item.id !== draft.id));
      setMessage(t('pdf.redactionSaved', { count: data.rule?.usage_count || 0 }));
    } catch (error) {
      setMessage(error.message || t('pdf.redactionSaveFailed'));
    } finally {
      setPdfRedactionSavingKey('');
    }
  };

  const deleteSavedRedactionRule = async (rule) => {
    const confirmed = window.confirm(t('pdf.confirmDeleteRule', { label: rule.label }));
    if (!confirmed) {
      return;
    }
    const deletingKey = `saved-${rule.id}`;
    setPdfRedactionSavingKey(deletingKey);
    try {
      const response = await apiFetch(`/redaction-rules/${rule.id}`, { method: 'DELETE' });
      if (!response.ok) {
        let detail = t('pdf.redactionDeleteFailed');
        try {
          const data = await response.json();
          detail = data.detail || detail;
        } catch (error) {
          detail = response.status === 401 ? t('pdf.notLoggedIn') : detail;
        }
        throw new Error(detail);
      }
      await refreshPdfRedactionSuggestions();
      setMessage(t('pdf.redactionDeleted'));
    } catch (error) {
      setMessage(error.message || t('pdf.redactionDeleteFailed'));
    } finally {
      setPdfRedactionSavingKey('');
    }
  };

  const closePdfPreview = () => {
    revokeObjectUrl(pdfPreview.url);
    revokeObjectUrl(pdfPreview.pageImageUrl);
    resetPdfRedactionState('');
    setPdfPreview({ url: '', title: '', page: null, open: false, documentId: null, fileName: '', pageImageUrl: '' });
  };

  const deleteStoredDocument = async (documentId, fileName) => {
    const confirmed = window.confirm(t('docs.confirmDelete', { name: fileName }));
    if (!confirmed) {
      return;
    }
    try {
      const response = await apiFetch(`/documents/${documentId}`, { method: 'DELETE' });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || t('docs.deleteFailed'));
      }
      if (pdfPreview.open && pdfPreview.title === fileName) {
        closePdfPreview();
      }
      await loadDocuments();
      setMessage(t('docs.deleteSuccess', { name: fileName }));
    } catch (error) {
      setMessage(error.message || t('docs.deleteFailed'));
    }
  };

  const findArchivedDocumentByFileName = (fileName) => documents.find((item) => item.file_name === fileName);

  const isFileArchived = (fileName) => !!findArchivedDocumentByFileName(fileName);

  const annotateSelectedFile = async (file) => {
    const matchedDocument = findArchivedDocumentByFileName(file.name || file.file_name);
    if (!matchedDocument) {
      setMessage(t('docs.annotateFirst'));
      return;
    }
    await previewStoredDocument(matchedDocument.id, matchedDocument.file_name);
    await enterPdfRedactionMode(matchedDocument.id, matchedDocument.file_name, 1);
  };

  const toggleYear = (year) => {
    setExpandedYears((current) => ({
      ...current,
      [year]: current[year] === undefined ? false : !current[year]
    }));
  };

  const toggleMonth = (year, month) => {
    const groupKey = `${year}-${month}`;
    setExpandedMonths((current) => ({
      ...current,
      [groupKey]: !current[groupKey]
    }));
  };

  useEffect(() => {
    loadCurrentUser();
    loadSecurityStatus();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      return;
    }
    loadHealth();
    loadTalkerLearning();
    loadDashboard();
    loadDocuments();
    loadUsers();
    loadFeatureSettings();
    loadSecurityStatus();
  }, [currentUser]);

  useEffect(() => {
    if (!featureSettings.talker_enabled && activeView === 'talker') {
      setActiveView('overview');
    }
  }, [activeView, featureSettings.talker_enabled]);

  useEffect(() => {
    if (!hasInitializedDashboardRangeEffect.current) {
      hasInitializedDashboardRangeEffect.current = true;
      return;
    }
    loadDashboardPeriod();
  }, [periodRange.startDate, periodRange.endDate]);

  useEffect(() => {
    if (!keyword || !dashboardData.assets.length) {
      return;
    }
    loadLedger(keyword);
    loadLifecycle(keyword);
  }, [dashboardData.assets.length, dashboardData.overview?.total_market_value_usd, keyword]);

  useEffect(() => {
    const years = Object.keys(documentTree);
    if (!years.length) {
      setExpandedYears({});
      setExpandedMonths({});
      return;
    }
    setExpandedYears((current) => {
      const next = { ...current };
      years.forEach((year) => {
        if (next[year] === undefined) {
          next[year] = false;
        }
      });
      return next;
    });
  }, [documents]);

  useEffect(() => {
    const fileNames = uniqueNonEmptyStrings(selectedFiles.map((file) => file.name));
    if (!fileNames.length) {
      return;
    }
    let cancelled = false;
    setRedactionStatusByFile((current) => {
      const next = { ...current };
      fileNames.forEach((fileName) => {
        if (!next[fileName]) {
          next[fileName] = { loading: true, hasRules: false, ruleCount: 0 };
        }
      });
      return next;
    });
    fileNames.forEach(async (fileName) => {
      try {
        const response = await apiFetch(`/redaction-rules/suggestions?file_name=${encodeURIComponent(fileName)}`);
        if (!response.ok) {
          throw new Error(t('pdf.redactionLoadFailed'));
        }
        const data = await response.json();
        if (cancelled) {
          return;
        }
        const ruleCount = (data.applied_rules || []).length + (data.learning_rules || []).length;
        setRedactionStatusByFile((current) => ({
          ...current,
          [fileName]: { loading: false, hasRules: ruleCount > 0, ruleCount }
        }));
      } catch (error) {
        if (cancelled) {
          return;
        }
        setRedactionStatusByFile((current) => ({
          ...current,
          [fileName]: { loading: false, hasRules: false, ruleCount: 0 }
        }));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selectedFiles]);

  useEffect(() => {
    if (!dashboardData.assets.length) {
      return;
    }
    const matchedAsset = dashboardData.assets.find((item) => item.asset_no === keyword);
    if (!matchedAsset) {
      const nextKeyword = dashboardData.assets[0].asset_no;
      selectAsset(nextKeyword);
    }
  }, [dashboardData.assets]);

  useEffect(() => () => {
    revokeObjectUrl(pdfPreview.url);
    revokeObjectUrl(pdfPreview.pageImageUrl);
  }, [pdfPreview]);

  const displayDashboard = dashboardData.assets || [];
  const livePulse = dashboardData.market_pulse || [];
  const overviewMetrics = dashboardData.overview;
  const periodSummary = dashboardData.period;
  const periodAssets = dashboardData.period_assets || [];
  const displayLedger = ledger;
  const ledgerPageSize = 5;
  const ledgerPageCount = Math.max(1, Math.ceil(displayLedger.length / ledgerPageSize));
  const safeLedgerPage = Math.min(ledgerPage, ledgerPageCount);
  const ledgerPageStart = (safeLedgerPage - 1) * ledgerPageSize;
  const pagedLedger = displayLedger.slice(ledgerPageStart, ledgerPageStart + ledgerPageSize);
  const ledgerPageNumbers = Array.from({ length: ledgerPageCount }, (_, index) => index + 1);
  const lifecycleMetrics = lifecycle?.metrics;
  const linkedEvents = useMemo(() => lifecycle?.linked_events || [], [lifecycle]);
  const financingLinkedEvents = linkedEvents.filter((item) => item.event_type === 'FINANCING');
  const lifecycleFinancingCostBreakdown = formatCurrencyBreakdown(
    Array.from(financingLinkedEvents.reduce((map, item) => {
      const currency = String(item?.secondary_currency || item?.currency || lifecycleMetrics?.currency || 'USD').trim().toUpperCase() || 'USD';
      const amount = Math.abs(Number(item?.secondary_amount || 0));
      if (amount <= 0.0001) {
        return map;
      }
      const current = map.get(currency) || { currency, amount: 0 };
      map.set(currency, { currency, amount: current.amount + amount });
      return map;
    }, new Map()).values())
  );
  const lifecycleFinancingCostLabel = lifecycleFinancingCostBreakdown.map((item) => `${item.currency} ${formatSigned(-item.amount)}`).join(' · ');
  const hasLifecycleFinancingCost = lifecycleFinancingCostBreakdown.length > 0;
  const fxLinkedEvents = linkedEvents.filter((item) => item.event_type === 'FX');
  const structuredLinkedEvents = linkedEvents.filter((item) => item.event_type === 'STRUCTURED_EVENT');
  const linkedEventTabs = [
    { key: 'ALL', titleKey: 'panel.linked.tab.all', subtitle: t('panel.linked.tab.allSub', { count: linkedEvents.length }), count: linkedEvents.length, eventType: 'ALL' },
    { key: 'FINANCING', titleKey: 'panel.linked.tab.financing', subtitle: financingLinkedEvents.length ? t('panel.linked.tab.since', { date: financingLinkedEvents[0]?.event_date || '-' }) : t('panel.linked.tab.empty'), count: financingLinkedEvents.length, eventType: 'FINANCING' },
    { key: 'STRUCTURED_EVENT', titleKey: 'panel.linked.tab.structured', subtitle: structuredLinkedEvents.length ? t('panel.linked.tab.since', { date: structuredLinkedEvents[0]?.event_date || '-' }) : t('panel.linked.tab.empty'), count: structuredLinkedEvents.length, eventType: 'STRUCTURED_EVENT' },
    { key: 'FX', titleKey: 'panel.linked.tab.fx', subtitle: fxLinkedEvents.length ? t('panel.linked.tab.since', { date: fxLinkedEvents[0]?.event_date || '-' }) : t('panel.linked.tab.empty'), count: fxLinkedEvents.length, eventType: 'FX' }
  ].filter((item) => item.key === 'ALL' || item.count > 0);
  const linkedEventRows = selectedLinkedEventKey === 'ALL'
    ? linkedEvents
    : linkedEvents.filter((item) => item.event_type === selectedLinkedEventKey);
  const assetSelectionOptions = displayDashboard.map((item) => ({
    asset_no: item.asset_no,
    label: `${item.asset_no} · ${item.security_name}`
  }));
  const linkedEventPageSize = 5;
  const linkedEventPageCount = Math.max(1, Math.ceil(linkedEventRows.length / linkedEventPageSize));
  const safeLinkedEventPage = Math.min(linkedEventPage, linkedEventPageCount);
  const linkedEventPageStart = (safeLinkedEventPage - 1) * linkedEventPageSize;
  const pagedLinkedEventRows = linkedEventRows.slice(linkedEventPageStart, linkedEventPageStart + linkedEventPageSize);
  const linkedEventPageNumbers = Array.from({ length: linkedEventPageCount }, (_, index) => index + 1);
  const currentAssets = displayDashboard.filter((item) => getAssetPositionStatus(item) === 'CURRENT');
  const exitedAssets = displayDashboard.filter((item) => getAssetPositionStatus(item) === 'EXITED');
  const currentAssetsMarketValueUsd = currentAssets.reduce((sum, item) => sum + Number(item.market_value_usd || 0), 0);
  const currentAssetsUnrealizedProfitUsd = currentAssets.reduce((sum, item) => sum + Number(item.unrealized_profit_usd || 0), 0);
  const exitedAssetsRealizedProfitUsd = exitedAssets.reduce((sum, item) => sum + Number(item.realized_profit_usd || 0), 0);
  const exitedAssetsDividendUsd = exitedAssets.reduce((sum, item) => sum + Number(item.total_dividend_usd || 0), 0);
  const analyticsSummaryCards = [
    {
      key: 'CURRENT',
      labelKey: 'position.current',
      count: currentAssets.length,
      detail: t('summary.current.detail', { mv: formatNumber(currentAssetsMarketValueUsd, 2), unrealized: formatSigned(currentAssetsUnrealizedProfitUsd) })
    },
    {
      key: 'EXITED',
      labelKey: 'position.exited',
      count: exitedAssets.length,
      detail: t('summary.exited.detail', { realized: formatSigned(exitedAssetsRealizedProfitUsd), dividend: formatSigned(exitedAssetsDividendUsd) })
    },
    {
      key: 'ALL',
      labelKey: 'position.all',
      count: displayDashboard.length,
      detail: t('summary.all.detail', { current: currentAssets.length, exited: exitedAssets.length })
    }
  ];
  const assetSummaryByNo = displayDashboard.reduce((map, item) => {
    if (item?.asset_no) {
      map[item.asset_no] = item;
    }
    return map;
  }, {});
  const financingInterestDetails = mapFinancingInterestDetails(
    Array.isArray(periodSummary?.financing_interest_details) ? periodSummary.financing_interest_details : [],
    assetSummaryByNo,
    buildCurrencyUsdRateMap(displayDashboard)
  );
  const overviewPositionLabel = getPositionFilterLabel(overviewPositionFilter, t);
  const overviewScopedAssets = getPositionScopedAssets(displayDashboard, overviewPositionFilter);
  const overviewScopedMarketValueUsd = overviewScopedAssets.reduce((sum, item) => sum + Number(item.market_value_usd || 0), 0);
  const overviewScopedDividendUsd = overviewScopedAssets.reduce((sum, item) => sum + Number(item.total_dividend_usd || 0), 0);
  const overviewScopedRealizedProfitUsd = overviewScopedAssets.reduce((sum, item) => sum + Number(item.realized_profit_usd || 0), 0);
  const overviewScopedUnrealizedProfitUsd = overviewScopedAssets.reduce((sum, item) => sum + Number(item.unrealized_profit_usd || 0), 0);
  const overviewInterestMetrics = getScopedFinancingInterestMetrics(
    financingInterestDetails,
    overviewPositionFilter,
    overviewScopedAssets,
    Number(periodSummary?.financing_interest_usd || 0),
    assetSummaryByNo
  );
  const overviewScopedInterestUsd = overviewInterestMetrics.scopedUsd;
  const overviewRankedAssets = [...overviewScopedAssets].sort((left, right) => {
    if (overviewPositionFilter === 'EXITED') {
      return Number(right.realized_profit_usd || 0) - Number(left.realized_profit_usd || 0);
    }
    return Number(right.market_value_usd || 0) - Number(left.market_value_usd || 0);
  });
  const focusAssets = overviewRankedAssets.slice(0, 5);
  const returnRankAssets = [...overviewScopedAssets].sort((a, b) => Number(b.total_return_rate || 0) - Number(a.total_return_rate || 0)).slice(0, 5);
  const selectedAssetSummary = displayDashboard.find((item) => item.asset_no === keyword) || null;
  const beginAssetEdit = () => {
    if (!lifecycleMetrics) {
      return;
    }
    setAssetEditForm({
      asset_no: lifecycleMetrics.asset_no || '',
      stock_ticker: lifecycleMetrics.stock_ticker || '',
      security_name: lifecycleMetrics.security_name || '',
      currency: lifecycleMetrics.currency || '',
      asset_type: lifecycleMetrics.asset_type || ''
    });
    setAssetEditMode(true);
    setMessage('');
  };
  const saveAssetEdit = async () => {
    if (!lifecycleMetrics || assetEditSaving) {
      return;
    }
    setAssetEditSaving(true);
    try {
      const response = await apiFetch(`/assets/${encodeURIComponent(lifecycleMetrics.asset_no)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(assetEditForm)
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || t('misc.assetSaveFailed'));
      }
      const nextAssetNo = data.asset?.asset_no || assetEditForm.asset_no;
      setKeyword(nextAssetNo);
      setAssetEditMode(false);
      setMessage(t('misc.assetSaved'));
      await loadDashboard();
      await loadLifecycle(nextAssetNo);
      await loadLedger(nextAssetNo);
    } catch (error) {
      setMessage(error.message || t('misc.assetSaveFailed'));
    } finally {
      setAssetEditSaving(false);
    }
  };
  const selectedAssetQuoteDate = String(selectedAssetSummary?.price_date || '').trim();
  const lifecycleQuoteDate = String(lifecycleMetrics?.price_date || '').trim();
  const shouldPreferDashboardQuote = Boolean(
    !lifecycleMetrics
    && selectedAssetSummary
    && Number(selectedAssetSummary?.latest_price || 0) > 0.0001
  );
  const displayedLifecyclePrice = shouldPreferDashboardQuote
    ? Number(selectedAssetSummary?.latest_price || 0)
    : Number(lifecycleMetrics?.latest_price || 0);
  const displayedLifecyclePriceDate = shouldPreferDashboardQuote
    ? (selectedAssetQuoteDate || lifecycleQuoteDate)
    : lifecycleQuoteDate;
  const hasOpenPosition = Number(lifecycleMetrics?.current_quantity || 0) > 0.0001;
  const displayedUnrealizedProfit = hasOpenPosition
    ? (displayedLifecyclePrice - Number(lifecycleMetrics?.average_cost || 0)) * Number(lifecycleMetrics?.current_quantity || 0)
    : Number(lifecycleMetrics?.unrealized_profit || 0);
  const normalizedAssetFilter = String(assetFilter || '').trim().toLowerCase();
  const normalizedOverviewFilter = String(overviewFilter || '').trim().toLowerCase();
  const unrealizedReturnRate = hasOpenPosition && Number(lifecycleMetrics?.average_cost || 0) > 0.0001
    ? ((displayedLifecyclePrice - Number(lifecycleMetrics?.average_cost || 0)) / Number(lifecycleMetrics?.average_cost || 1)) * 100
    : null;
  const analyticsScopedAssets = getPositionScopedAssets(displayDashboard, analyticsPositionFilter);
  const filteredAssets = normalizedAssetFilter
    ? analyticsScopedAssets.filter((item) => {
        const securityName = String(item.security_name || '').toLowerCase();
        const assetNo = String(item.asset_no || '').toLowerCase();
        const currency = String(item.currency || '').toLowerCase();
        return securityName.includes(normalizedAssetFilter) || assetNo.includes(normalizedAssetFilter) || currency.includes(normalizedAssetFilter);
      })
    : analyticsScopedAssets;
  const analyticsPageSize = 10;
  const analyticsPageCount = Math.max(1, Math.ceil(filteredAssets.length / analyticsPageSize));
  const safeAnalyticsPage = Math.min(analyticsPage, analyticsPageCount);
  const analyticsPageStart = (safeAnalyticsPage - 1) * analyticsPageSize;
  const pagedAnalyticsAssets = filteredAssets.slice(analyticsPageStart, analyticsPageStart + analyticsPageSize);
  const analyticsPageNumbers = Array.from({ length: analyticsPageCount }, (_, index) => index + 1);
  const workspaceBaseAssetsWithMetrics = periodAssets.map((item) => {
    const matchedAsset = displayDashboard.find((asset) => asset.asset_no === item.asset_no);
    const positionStatus = matchedAsset ? getAssetPositionStatus(matchedAsset) : 'INACTIVE';
    return {
      ...item,
      position_status: positionStatus,
      ever_held: Boolean(matchedAsset?.ever_held),
      total_return_rate: Number(matchedAsset?.total_return_rate || 0),
      current_market_value_usd: Number(matchedAsset?.market_value_usd || item.market_value_usd || 0)
    };
  });
  const workspaceCurrentAssets = workspaceBaseAssetsWithMetrics.filter((item) => getAssetPositionStatus(item) === 'CURRENT');
  const workspaceExitedAssets = workspaceBaseAssetsWithMetrics.filter((item) => getAssetPositionStatus(item) === 'EXITED');
  const workspacePositionLabel = getPositionFilterLabel(workspacePositionFilter, t);
  const workspacePositionScopedAssets = getPositionScopedAssets(workspaceBaseAssetsWithMetrics, workspacePositionFilter);
  const workspaceVisibleAssets = normalizedOverviewFilter
    ? workspacePositionScopedAssets.filter((item) => {
        const securityName = String(item.security_name || '').toLowerCase();
        const assetNo = String(item.asset_no || '').toLowerCase();
        const currency = String(item.currency || '').toLowerCase();
        return securityName.includes(normalizedOverviewFilter) || assetNo.includes(normalizedOverviewFilter) || currency.includes(normalizedOverviewFilter);
      })
    : workspacePositionScopedAssets;
  const workspaceScopedMarketValueUsd = workspacePositionScopedAssets.reduce((sum, item) => sum + Number(item.current_market_value_usd || 0), 0);
  const workspaceScopedBuyAmountUsd = workspacePositionScopedAssets.reduce((sum, item) => sum + Number(item.buy_amount_usd || 0), 0);
  const workspaceScopedSellAmountUsd = workspacePositionScopedAssets.reduce((sum, item) => sum + Number(item.sell_amount_usd || 0), 0);
  const workspaceScopedDividendUsd = workspacePositionScopedAssets.reduce((sum, item) => sum + Number(item.dividend_amount_usd || 0), 0);
  const workspaceScopedRealizedProfitUsd = workspacePositionScopedAssets.reduce((sum, item) => sum + Number(item.realized_profit_usd || 0), 0);
  const workspaceScopedUnrealizedProfitUsd = workspacePositionScopedAssets.reduce((sum, item) => sum + Number(item.unrealized_profit_usd || 0), 0);
  const workspaceInterestMetrics = getScopedFinancingInterestMetrics(
    financingInterestDetails,
    workspacePositionFilter,
    getPositionScopedAssets(displayDashboard, workspacePositionFilter),
    Number(periodSummary?.financing_interest_usd || 0),
    assetSummaryByNo
  );
  const workspaceScopedInterestUsd = workspaceInterestMetrics.scopedUsd;
  const workspacePositionSummaryCards = [
    {
      key: 'CURRENT',
      labelKey: 'position.current',
      count: workspaceCurrentAssets.length,
      detail: t('summary.current.detail', { mv: formatNumber(workspaceCurrentAssets.reduce((sum, item) => sum + Number(item.current_market_value_usd || 0), 0), 2), unrealized: formatSigned(workspaceCurrentAssets.reduce((sum, item) => sum + Number(item.unrealized_profit_usd || 0), 0)) })
    },
    {
      key: 'EXITED',
      labelKey: 'position.exited',
      count: workspaceExitedAssets.length,
      detail: t('summary.exited.detail', { realized: formatSigned(workspaceExitedAssets.reduce((sum, item) => sum + Number(item.realized_profit_usd || 0), 0)), dividend: formatSigned(workspaceExitedAssets.reduce((sum, item) => sum + Number(item.dividend_amount_usd || 0), 0)) })
    },
    {
      key: 'ALL',
      labelKey: 'position.all',
      count: workspaceBaseAssetsWithMetrics.length,
      detail: t('summary.all.detail', { current: workspaceCurrentAssets.length, exited: workspaceExitedAssets.length })
    }
  ];
  const workspaceSortColumns = [
    { key: 'security_name', labelKey: 'col.name', type: 'text' },
    { key: 'currency', labelKey: 'col.currency', type: 'text' },
    { key: 'current_market_value_usd', labelKey: 'col.mvUsd', type: 'number' },
    { key: 'buy_amount_usd', labelKey: 'col.buyUsd', type: 'number' },
    { key: 'sell_amount_usd', labelKey: 'col.sellUsd', type: 'number' },
    { key: 'dividend_amount_usd', labelKey: 'col.dividendUsd', type: 'number' },
    { key: 'realized_profit_usd', labelKey: 'col.realizedUsd', type: 'number' },
    { key: 'unrealized_profit_usd', labelKey: 'col.unrealized', type: 'number' }
  ];
  const workspaceSortColumnMap = workspaceSortColumns.reduce((map, column) => ({ ...map, [column.key]: column }), {});
  const sortedWorkspaceAssetsWithMetrics = [...workspaceVisibleAssets].sort((left, right) => {
    const column = workspaceSortColumnMap[workspaceSort.key] || workspaceSortColumns[0];
    const directionMultiplier = workspaceSort.direction === 'asc' ? 1 : -1;
    if (column.type === 'number') {
      return (Number(left[column.key] || 0) - Number(right[column.key] || 0)) * directionMultiplier;
    }
    return String(left[column.key] || '').localeCompare(String(right[column.key] || ''), 'zh-CN', { numeric: true }) * directionMultiplier;
  });
  const toggleWorkspaceSort = (key) => {
    setWorkspaceSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
    setWorkspacePage(1);
  };
  const workspacePageSize = 10;
  const workspacePageCount = Math.max(1, Math.ceil(sortedWorkspaceAssetsWithMetrics.length / workspacePageSize));
  const safeWorkspacePage = Math.min(workspacePage, workspacePageCount);
  const workspacePageStart = (safeWorkspacePage - 1) * workspacePageSize;
  const pagedWorkspaceAssets = sortedWorkspaceAssetsWithMetrics.slice(workspacePageStart, workspacePageStart + workspacePageSize);
  const workspacePageNumbers = Array.from({ length: workspacePageCount }, (_, index) => index + 1);
  const workspaceScopeChip = { currency: 'scope', label: t('workspace.scopeChip', { label: workspacePositionLabel, count: workspacePositionScopedAssets.length }) };
  const workspaceInterestChip = { currency: 'scope', label: workspacePositionFilter === 'ALL' ? t('workspace.interestChipAll') : t('workspace.interestChipScoped', { count: workspaceInterestMetrics.matchedCount }) };
  const workspaceInterestBreakdown = [
    workspaceInterestChip,
    ...(workspaceInterestMetrics.unassignedCount ? [{ currency: 'scope', label: t('workspace.interestUnassigned', { count: workspaceInterestMetrics.unassignedCount, value: formatNumber(workspaceInterestMetrics.unassignedUsd, 2) }) }] : [])
  ];
  const workspaceMetricCards = [
    {
      key: 'buy',
      labelKey: 'workspace.buy',
      usdDisplay: formatNumber(workspaceScopedBuyAmountUsd, 2),
      breakdown: [workspaceScopeChip],
      toneClass: ''
    },
    {
      key: 'sell',
      labelKey: 'workspace.sell',
      usdDisplay: formatNumber(workspaceScopedSellAmountUsd, 2),
      breakdown: [workspaceScopeChip],
      toneClass: ''
    },
    {
      key: 'dividend',
      labelKey: 'workspace.dividend',
      usdDisplay: formatSigned(workspaceScopedDividendUsd),
      breakdown: [workspaceScopeChip],
      toneClass: 'positive',
      subLabel: t('workspace.currentMv', { value: formatNumber(workspaceScopedMarketValueUsd, 2) })
    },
    {
      key: 'realized',
      labelKey: 'workspace.realized',
      usdDisplay: formatSigned(workspaceScopedRealizedProfitUsd),
      breakdown: [workspaceScopeChip],
      toneClass: workspaceScopedRealizedProfitUsd >= 0 ? 'positive' : 'negative',
      subLabel: t('workspace.unrealized', { value: formatSigned(workspaceScopedUnrealizedProfitUsd) })
    },
    {
      key: 'interest',
      labelKey: workspacePositionFilter === 'ALL' ? 'workspace.interest' : 'workspace.interestScopedShort',
      usdDisplay: formatNumber(workspaceScopedInterestUsd, 2),
      breakdown: workspaceInterestBreakdown,
      toneClass: 'negative',
      actionLabel: workspaceInterestMetrics.displayDetails.length ? t('workspace.viewDetails', { count: workspaceInterestMetrics.displayDetails.length }) : ''
    }
  ];
  const workspaceTotalReturnUsd = workspaceScopedRealizedProfitUsd + workspaceScopedDividendUsd - workspaceScopedInterestUsd;
  const workspaceExportRows = sortedWorkspaceAssetsWithMetrics.map((item) => ({
    [t('col.name')]: item.security_name,
    [t('col.currency')]: item.currency,
    [t('col.mvUsd')]: item.market_value_usd,
    [t('col.unrealized')]: item.unrealized_profit_usd,
    [t('analytics.unrealizedProfit')]: item.unrealized_return_rate,
    [t('col.buyUsd')]: item.buy_amount_usd,
    [t('col.sellUsd')]: item.sell_amount_usd,
    [t('col.dividendUsd')]: item.dividend_amount_usd,
    [t('workspace.annualized', { value: '' })]: item.dividend_annualized_rate,
    [t('col.realizedUsd')]: item.realized_profit_usd,
    [t('overview.returnRate', { value: '' })]: item.realized_return_rate
  }));
  useEffect(() => {
    setWorkspacePage(1);
  }, [overviewFilter, workspacePositionFilter, periodRange.startDate, periodRange.endDate, periodAssets.length]);
  useEffect(() => {
    setAnalyticsPage(1);
  }, [assetFilter, analyticsPositionFilter, displayDashboard.length]);
  useEffect(() => {
    if (!filteredAssets.length) {
      return;
    }
    const matchedVisibleAsset = filteredAssets.find((item) => item.asset_no === keyword);
    if (!matchedVisibleAsset) {
      const nextKeyword = filteredAssets[0].asset_no;
      if (nextKeyword && nextKeyword !== keyword) {
        selectAsset(nextKeyword);
      }
    }
  }, [filteredAssets, keyword]);
  useEffect(() => {
    setLedgerPage(1);
  }, [keyword, displayLedger.length]);
  useEffect(() => {
    setLinkedEventPage(1);
  }, [keyword, selectedLinkedEventKey, linkedEventRows.length]);
  useEffect(() => {
    const nextSelections = {};
    linkedEvents.forEach((item) => {
      if (item.event_type === 'FINANCING' && item.reference_no) {
        nextSelections[item.reference_no] = manualLinkedAssets[item.reference_no] || '';
      }
    });
    setManualLinkedAssets(nextSelections);
  }, [keyword, linkedEvents]);
  useEffect(() => {
    setSelectedLinkedEventKey('ALL');
  }, [keyword, linkedEvents.length]);
  useEffect(() => {
    if (linkedEvents.length) {
      setSelectedAnalyticsPanel('LINKED');
    } else {
      setSelectedAnalyticsPanel('LIFECYCLE');
    }
  }, [keyword, linkedEvents.length]);
  useEffect(() => {
    window.localStorage.setItem(themeStorageKey, theme);
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.dataset.theme = theme;
    }
  }, [theme]);
  const documentTree = documents.reduce((tree, document) => {
    const { year, month } = getDocumentPeriodParts(document, t);
    const yearNode = tree[year] || {};
    const monthNode = yearNode[month] || [];
    monthNode.push(document);
    yearNode[month] = monthNode;
    tree[year] = yearNode;
    return tree;
  }, {});
  const fileGroups = selectedFiles.reduce((groups, file) => {
    const period = getFilePeriodLabel(file, extractionItems, t);
    const group = groups[period] || [];
    group.push(file);
    groups[period] = group;
    return groups;
  }, {});

  if (authChecking || securityChecking) {
    return (
      <div className="terminal-shell" data-theme={theme}>
        <div className="page authPageShell">
          <section className="panel authPanel">
            <div className="panelHeader">
              <span className="panelKicker">{securityChecking ? 'Security' : 'Authentication'}</span>
              <h2>{securityChecking ? t('security.checking') : t('auth.checking')}</h2>
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (securityStatus.encryption_enabled && !securityStatus.unlocked) {
    return (
      <div className="terminal-shell" data-theme={theme}>
        <div className="page authPageShell">
          <section className="panel authPanel masterKeyPanel">
            <div className="panelHeader">
              <span className="panelKicker">Security</span>
              <h2>{t('security.title')}</h2>
              <p>{t('security.hint')}</p>
            </div>
            <div className="masterKeyWarning">
              <p>{t('security.warning')}</p>
            </div>
            {message ? <p className="messageBanner">{message}</p> : null}
            <div className="authFormGrid">
              <label className="authField">
                <span>{t('security.masterKey')}</span>
                <input type="password" value={masterKeyInput} onChange={(event) => setMasterKeyInput(event.target.value)} placeholder={t('security.keyPlaceholder')} onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    submitMasterKey();
                  }
                }} />
              </label>
            </div>
            <div className="authActionsRow">
              <button className="primaryButton" onClick={submitMasterKey} disabled={masterKeyLoading}>{masterKeyLoading ? t('security.unlocking') : t('security.unlock')}</button>
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="terminal-shell" data-theme={theme}>
        <div className="page authPageShell">
          <section className="panel authPanel">
            <div className="panelHeader">
              <span className="panelKicker">Authentication</span>
              <h2>{t('auth.title')}</h2>
              <p>{t('auth.hint')}</p>
            </div>
            {message ? <p className="messageBanner">{message}</p> : null}
            <div className="authFormGrid">
              <label className="authField">
                <span>{t('auth.username')}</span>
                <input value={loginForm.username} onChange={(event) => setLoginForm((current) => ({ ...current, username: event.target.value }))} placeholder={t('auth.usernamePlaceholder')} />
              </label>
              <label className="authField">
                <span>{t('auth.password')}</span>
                <input type="password" value={loginForm.password} onChange={(event) => setLoginForm((current) => ({ ...current, password: event.target.value }))} placeholder={t('auth.passwordPlaceholder')} onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    submitLogin();
                  }
                }} />
              </label>
            </div>
            <div className="authActionsRow">
              <button className="primaryButton" onClick={submitLogin} disabled={loginLoading}>{loginLoading ? t('auth.loggingIn') : t('auth.login')}</button>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="terminal-shell" data-theme={theme}>
      <div className="page">
        <div className="topbar">
          <div className="brandBlock">
            <div className="terminalTag">IIT TERMINAL</div>
            <h1>Investment Intelligence Tracker</h1>
            <p>Professional multi-asset intelligence workspace for OCR-ledger analytics</p>
          </div>
          <div className="topbarActions">
            <LanguageToggle />
            <div className="themeSwitcher" role="group" aria-label={t('theme.aria')}>
              {themeOptions.map((option) => (
                <button
                  key={option.id}
                  className={theme === option.id ? 'themeOptionButton compactThemeOption activeThemeOptionButton' : 'themeOptionButton compactThemeOption'}
                  onClick={() => setTheme(option.id)}
                  title={t(option.labelKey)}
                  aria-label={t(option.labelKey)}
                >
                  <span className="themeSwatch" style={{ '--theme-swatch': option.accent }} />
                  <span className="themeOptionLabel">{t(option.labelKey)}</span>
                </button>
              ))}
            </div>
            <button className="accountActionButton" onClick={logout} title={t('account.signOut')}>
              <span className="accountActionPrimary">{String(currentUser.username || '').toUpperCase()}</span>
              <span className="accountActionSecondary">{currentUser.role === 'admin' ? t('account.admin') : t('account.user')}</span>
              <span className="accountActionTertiary">{t('account.signOut')}</span>
            </button>
          </div>
        </div>

        <div className="viewTabStrip desktopOnly">
          <button className={activeView === 'overview' ? 'viewTab activeViewTab' : 'viewTab'} onClick={() => setActiveView('overview')}><ChartPie weight="duotone" /> {t('nav.overview')}</button>
          <button className={activeView === 'documents' ? 'viewTab activeViewTab' : 'viewTab'} onClick={() => setActiveView('documents')}><Folder weight="duotone" /> {t('nav.documents')}</button>
          <button className={activeView === 'workspace' ? 'viewTab activeViewTab' : 'viewTab'} onClick={() => setActiveView('workspace')}><Briefcase weight="duotone" /> {t('nav.workspace')}</button>
          <button className={activeView === 'analytics' ? 'viewTab activeViewTab' : 'viewTab'} onClick={() => setActiveView('analytics')}><ChartLineUp weight="duotone" /> {t('nav.analytics')}</button>
          {featureSettings.talker_enabled ? <button className={activeView === 'talker' ? 'viewTab activeViewTab' : 'viewTab'} onClick={() => setActiveView('talker')}><ChatCircle weight="duotone" /> {t('nav.talker')}</button> : null}
          <button className={activeView === 'monitor' ? 'viewTab activeViewTab' : 'viewTab'} onClick={() => setActiveView('monitor')}><Monitor weight="duotone" /> {t('nav.monitor')}</button>
          {currentUser.role === 'admin' ? <button className={activeView === 'configure' ? 'viewTab activeViewTab' : 'viewTab'} onClick={() => setActiveView('configure')}><Gear weight="duotone" /> {t('nav.configure')}</button> : null}
        </div>

        {activeView === 'overview' ? (
          <OverviewPanel
            isMobileViewport={isMobileViewport}
            livePulse={livePulse}
            overviewPositionFilter={overviewPositionFilter}
            setOverviewPositionFilter={setOverviewPositionFilter}
            positionFilters={analyticsPositionFilters}
            summaryCards={analyticsSummaryCards}
            overviewPositionLabel={overviewPositionLabel}
            scopedMarketValueUsd={overviewScopedMarketValueUsd}
            scopedDividendUsd={overviewScopedDividendUsd}
            scopedRealizedProfitUsd={overviewScopedRealizedProfitUsd}
            scopedUnrealizedProfitUsd={overviewScopedUnrealizedProfitUsd}
            scopedAssetCount={overviewScopedAssets.length}
            scopedAssets={overviewScopedAssets}
            returnRankAssets={returnRankAssets}
            apiFetch={apiFetch}
            formatNumber={formatNumber}
            formatSigned={formatSigned}
            getPulseValueDigits={getPulseValueDigits}
            onOpenAsset={(assetNo) => {
              setActiveView('analytics');
              selectAsset(assetNo);
            }}
          />
        ) : null}

        {activeView === 'workspace' ? (
          <section className="panel">
            <div className="panelHeader rowHeader">
              <div>
                <span className="panelKicker">Portfolio Workspace</span>
                <h2>{t('workspace.title')}</h2>
              </div>
              <div className="topbarActions">
                <button className="ghostButton mobileHiddenAction" onClick={() => downloadCsv(workspaceExportRows, `portfolio-workspace-${periodRange.startDate || 'all'}-${periodRange.endDate || 'latest'}.csv`)}>{t('workspace.exportCsv')}</button>
                <span className="pillTag">{t('workspace.resultCount', { label: workspacePositionLabel, count: sortedWorkspaceAssetsWithMetrics.length })}</span>
              </div>
            </div>

            <div className="workspaceControlGrid">
              <div className="selectionPanel workspaceControlPanel">
                <div className="panelHeader compactHeader">
                  <span className="panelKicker">Quick Range</span>
                  <h2>{t('workspace.quickRange')}</h2>
                </div>
                <div className="workspaceToolbarRow">
                  <div className="quickFilterRow workspaceToolbarGroup">
                    <button className="secondaryButton" onClick={() => { setMonthPicker(''); setPeriodRange({ startDate: '', endDate: '' }); }}>{t('workspace.all')}</button>
                    <button className="secondaryButton" onClick={() => { const now = new Date(); const start = `${now.getFullYear()}-01-01`; const end = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`; setMonthPicker(''); setPeriodRange({ startDate: start, endDate: end }); }}>YTD</button>
                    <button className="secondaryButton" onClick={() => { const end = new Date(); const start = new Date(); start.setDate(end.getDate() - 30); setMonthPicker(''); setPeriodRange({ startDate: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`, endDate: `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}` }); }}>{t('workspace.recent30')}</button>
                  </div>
                  <div className="workspaceDateGrid workspaceToolbarGroup">
                    <input ref={monthInputRef} type="month" value={monthPicker} onClick={() => openNativePicker(monthInputRef)} onFocus={() => openNativePicker(monthInputRef)} onChange={(event) => { const nextMonth = event.target.value; setMonthPicker(nextMonth); setPeriodRange(getMonthRange(nextMonth)); }} />
                    <input ref={startDateInputRef} type="date" value={periodRange.startDate} onClick={() => openNativePicker(startDateInputRef)} onFocus={() => openNativePicker(startDateInputRef)} onChange={(event) => { setMonthPicker(''); setPeriodRange((current) => ({ ...current, startDate: event.target.value })); }} />
                    <input ref={endDateInputRef} type="date" value={periodRange.endDate} onClick={() => openNativePicker(endDateInputRef)} onFocus={() => openNativePicker(endDateInputRef)} onChange={(event) => { setMonthPicker(''); setPeriodRange((current) => ({ ...current, endDate: event.target.value })); }} />
                  </div>
                  <div className="workspaceRangeHint workspaceToolbarHint">{`${formatCompactDate(periodRange.startDate, t)} ~ ${formatCompactDate(periodRange.endDate, t)}`}</div>
                </div>
              </div>

              <div className="selectionPanel workspaceControlPanel">
                <div className="panelHeader compactHeader">
                  <span className="panelKicker">Result Filter</span>
                  <h2>{t('workspace.resultFilter')}</h2>
                </div>
                <div className="analyticsFilterBar singleSearchBar">
                  <input value={overviewFilter} onChange={(event) => setOverviewFilter(event.target.value)} placeholder={t('workspace.filterPlaceholder')} />
                  <button className="secondaryButton" onClick={() => setOverviewFilter('')}>{t('workspace.clear')}</button>
                </div>
                <div className="analyticsPositionFilterRow desktopOnly">
                  {analyticsPositionFilters.map((option) => (
                    <button
                      key={`workspace-${option.key}`}
                      className={workspacePositionFilter === option.key ? 'analyticsPositionFilterButton activeAnalyticsPositionFilterButton' : 'analyticsPositionFilterButton'}
                      onClick={() => setWorkspacePositionFilter(option.key)}
                    >
                      {t(option.labelKey)}
                    </button>
                  ))}
                </div>
                <div className="analyticsPositionStatsGrid">
                  {workspacePositionSummaryCards.map((item) => (
                    <button
                      key={`workspace-card-${item.key}`}
                      className={workspacePositionFilter === item.key ? 'analyticsPositionStatCard activeAnalyticsPositionStatCard' : 'analyticsPositionStatCard'}
                      onClick={() => setWorkspacePositionFilter(item.key)}
                    >
                      <span>{t(item.labelKey)}</span>
                      <strong>{item.count}</strong>
                      <em>{item.detail}</em>
                    </button>
                  ))}
                </div>
                <div className="workspaceFormulaCard">
                  <div className="workspaceFormulaTitleRow">
                    <span className="panelKicker">Total Return Bridge</span>
                    <strong className={workspaceTotalReturnUsd >= 0 ? 'positive' : 'negative'}>{t('workspace.totalReturn', { value: formatSigned(workspaceTotalReturnUsd) })}</strong>
                  </div>
                  <div className="desktopOnly workspaceFormulaFlow" aria-label={t('workspace.formulaAria')}>
                    <div className="workspaceFormulaNode positiveTone">
                      <span>{t('workspace.dividend')}</span>
                      <strong>{formatSigned(workspaceScopedDividendUsd)}</strong>
                    </div>
                    <span className="workspaceFormulaOperator">+</span>
                    <div className={workspaceScopedRealizedProfitUsd >= 0 ? 'workspaceFormulaNode positiveTone' : 'workspaceFormulaNode negativeTone'}>
                      <span>{t('workspace.realized')}</span>
                      <strong>{formatSigned(workspaceScopedRealizedProfitUsd)}</strong>
                    </div>
                    <span className="workspaceFormulaOperator">−</span>
                    <div className="workspaceFormulaNode negativeTone">
                      <span>{workspacePositionFilter === 'ALL' ? t('workspace.interestAll') : t('workspace.interestScoped')}</span>
                      <strong>{formatNumber(workspaceScopedInterestUsd, 2)}</strong>
                    </div>
                    <span className="workspaceFormulaOperator">=</span>
                    <div className={workspaceTotalReturnUsd >= 0 ? 'workspaceFormulaNode totalTone positiveTone' : 'workspaceFormulaNode totalTone negativeTone'}>
                      <span>{t('workspace.totalReturnLabel')}</span>
                      <strong>{formatSigned(workspaceTotalReturnUsd)}</strong>
                    </div>
                  </div>
                  <details className="mobileOnly workspaceFormulaDisclosure">
                    <summary>{t('workspace.viewCalc')}</summary>
                    <div className="workspaceFormulaFlow" aria-label={t('workspace.formulaAria')}>
                      <div className="workspaceFormulaNode positiveTone">
                        <span>{t('workspace.dividend')}</span>
                        <strong>{formatSigned(workspaceScopedDividendUsd)}</strong>
                      </div>
                      <span className="workspaceFormulaOperator">+</span>
                      <div className={workspaceScopedRealizedProfitUsd >= 0 ? 'workspaceFormulaNode positiveTone' : 'workspaceFormulaNode negativeTone'}>
                        <span>{t('workspace.realized')}</span>
                        <strong>{formatSigned(workspaceScopedRealizedProfitUsd)}</strong>
                      </div>
                      <span className="workspaceFormulaOperator">−</span>
                      <div className="workspaceFormulaNode negativeTone">
                        <span>{workspacePositionFilter === 'ALL' ? t('workspace.interestAll') : t('workspace.interestScoped')}</span>
                        <strong>{formatNumber(workspaceScopedInterestUsd, 2)}</strong>
                      </div>
                      <span className="workspaceFormulaOperator">=</span>
                      <div className={workspaceTotalReturnUsd >= 0 ? 'workspaceFormulaNode totalTone positiveTone' : 'workspaceFormulaNode totalTone negativeTone'}>
                        <span>{t('workspace.totalReturnLabel')}</span>
                        <strong>{formatSigned(workspaceTotalReturnUsd)}</strong>
                      </div>
                    </div>
                  </details>
                </div>
                <div className="workspaceMetricGrid">
                  {workspaceMetricCards.map((card) => (
                    <div key={card.key} className="workspaceMetricCard">
                      <div className="workspaceMetricHeader">
                        <span>{t('workspace.metricLabel', { label: t(card.labelKey) })}</span>
                        <div className="workspaceMetricValueBlock">
                          <strong className={card.toneClass}>{card.usdDisplay}</strong>
                          {card.subLabel ? <em className="workspaceMetricSubLabel">{card.subLabel}</em> : null}
                        </div>
                      </div>
                      <div className="workspaceMetricBreakdown">
                        {card.breakdown.length ? card.breakdown.map((item) => (
                          <span key={`${card.key}-${item.currency}`} className="workspaceMetricChip">{item.label}</span>
                        )) : <span className="workspaceMetricChip muted">{t('workspace.noBreakdown')}</span>}
                      </div>
                      {card.key === 'interest' && card.actionLabel ? <button className="ghostButton compactActionButton workspaceMetricActionButton" onClick={() => setInterestDetailDrawerOpen(true)}>{card.actionLabel}</button> : null}
                    </div>
                  ))}
                </div>
                <p className="workspaceSummaryHint desktopOnly">{workspacePositionFilter === 'ALL' ? t('workspace.hintAll') : t('workspace.hintScoped', { label: workspacePositionLabel })}</p>
                <details className="mobileOnly calloutDisclosure">
                  <summary>{t('overview.formulaHint')}</summary>
                  <p className="workspaceSummaryHint">{workspacePositionFilter === 'ALL' ? t('workspace.hintAll') : t('workspace.hintScoped', { label: workspacePositionLabel })}</p>
                </details>
              </div>
            </div>

            <div className="workspaceListHeader">
              <div className="workspaceListMeta">
                <strong>{t('workspace.listHeader', { label: workspacePositionLabel })}</strong>
                <span>{t('workspace.pageInfo', { page: safeWorkspacePage, total: workspacePageCount, count: sortedWorkspaceAssetsWithMetrics.length })}</span>
              </div>
              {sortedWorkspaceAssetsWithMetrics.length ? (
                <div className="workspacePager">
                  <button className="ghostButton compactPagerButton" onClick={() => setWorkspacePage((current) => Math.max(1, current - 1))} disabled={safeWorkspacePage <= 1}>{t('workspace.prevPage')}</button>
                  <div className="workspacePageNumberRow">
                    {workspacePageNumbers.map((pageNumber) => (
                      <button
                        key={`workspace-page-${pageNumber}`}
                        className={pageNumber === safeWorkspacePage ? 'workspacePageNumberButton activeWorkspacePageNumberButton' : 'workspacePageNumberButton'}
                        onClick={() => setWorkspacePage(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    ))}
                  </div>
                  <button className="ghostButton compactPagerButton" onClick={() => setWorkspacePage((current) => Math.min(workspacePageCount, current + 1))} disabled={safeWorkspacePage >= workspacePageCount}>{t('workspace.nextPage')}</button>
                </div>
              ) : null}
            </div>

            <div className="mobileOnly workspaceAssetCards">
              {pagedWorkspaceAssets.length ? pagedWorkspaceAssets.map((item) => (
                <button
                  key={`mobile-workspace-${item.asset_no || item.security_name}`}
                  type="button"
                  className="workspaceAssetCard"
                  onClick={() => {
                    setActiveView('analytics');
                    selectAsset(item.asset_no);
                  }}
                >
                  <div className="workspaceAssetCardHeader">
                    <strong>{item.security_name}</strong>
                    <span>{item.currency || '-'}</span>
                  </div>
                  <div className="workspaceAssetCardGrid">
                    <span>{t('workspace.cardMv')}</span><strong>{formatNumber(item.current_market_value_usd, 2)}</strong>
                    <span>{t('workspace.cardBuy')}</span><strong>{formatNumber(item.buy_amount_usd, 2)}</strong>
                    <span>{t('workspace.cardSell')}</span><strong>{formatNumber(item.sell_amount_usd, 2)}</strong>
                    <span>{t('workspace.cardDividend')}</span><strong className="positive">{formatSigned(item.dividend_amount_usd)}</strong>
                    <span>{t('workspace.cardRealized')}</span><strong className={Number(item.realized_profit_usd) >= 0 ? 'positive' : 'negative'}>{formatSigned(item.realized_profit_usd)}</strong>
                    <span>{t('workspace.cardUnrealized')}</span><strong className={Number(item.unrealized_profit_usd) >= 0 ? 'positive' : 'negative'}>{formatSigned(item.unrealized_profit_usd)}</strong>
                  </div>
                  <div className="workspaceAssetCardFooter">{t('workspace.cardViewLifecycle')}</div>
                </button>
              )) : <div className="emptyState">{t('workspace.empty')}</div>}
            </div>

            <div className="desktopOnly tableWrap darkTableWrap workspaceResultTableWrap">
              <table>
                <colgroup>
                  <col className="workspaceNameColumn" />
                  <col className="workspaceCurrencyColumn" />
                  <col className="workspaceAmountColumn" />
                  <col className="workspaceAmountColumn" />
                  <col className="workspaceAmountColumn" />
                  <col className="workspaceMetricColumn" />
                  <col className="workspaceMetricColumn" />
                  <col className="workspaceMetricColumn" />
                </colgroup>
                <thead>
                  <tr>
                    {workspaceSortColumns.map((column) => (
                      <th key={column.key}>
                        <button className="workspaceSortHeaderButton" onClick={() => toggleWorkspaceSort(column.key)}>
                          <span>{t(column.labelKey)}</span>
                          <span className={workspaceSort.key === column.key ? 'workspaceSortIndicator activeWorkspaceSortIndicator' : 'workspaceSortIndicator'}>
                            {workspaceSort.key === column.key ? (workspaceSort.direction === 'asc' ? '↑' : '↓') : '↕'}
                          </span>
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedWorkspaceAssets.length ? pagedWorkspaceAssets.map((item) => (
                    <tr
                      key={item.asset_no || `${item.security_name}-${item.currency}`}
                      className="workspaceResultRow"
                      onClick={() => {
                        setActiveView('analytics');
                        selectAsset(item.asset_no);
                      }}
                      title={item.security_name}
                    >
                      <td>
                        <div className="workspaceResultNameCell">
                          <strong>{item.security_name}</strong>
                        </div>
                      </td>
                      <td>{item.currency}</td>
                      <td className="workspaceNumericCell">{formatNumber(item.current_market_value_usd, 2)}</td>
                      <td className="workspaceNumericCell">{formatNumber(item.buy_amount_usd, 2)}</td>
                      <td className="workspaceNumericCell">{formatNumber(item.sell_amount_usd, 2)}</td>
                      <td className="positive workspaceMetricDataCell">
                        <div className="workspaceResultMetricCell positive">
                          <strong>{formatSigned(item.dividend_amount_usd)}</strong>
                          <span>{t('workspace.annualized', { value: formatPercent(item.dividend_annualized_rate, 2) })}</span>
                        </div>
                      </td>
                      <td className={Number(item.realized_profit_usd) >= 0 ? 'positive workspaceMetricDataCell' : 'negative workspaceMetricDataCell'}>
                        <div className={Number(item.realized_profit_usd) >= 0 ? 'workspaceResultMetricCell positive' : 'workspaceResultMetricCell negative'}>
                          <strong>{formatSigned(item.realized_profit_usd)}</strong>
                          <span>{formatSignedPercent(item.realized_return_rate, 2)}</span>
                        </div>
                      </td>
                      <td className={Number(item.unrealized_profit_usd) >= 0 ? 'positive workspaceMetricDataCell' : 'negative workspaceMetricDataCell'}>
                        <div className={Number(item.unrealized_profit_usd) >= 0 ? 'workspaceResultMetricCell positive' : 'workspaceResultMetricCell negative'}>
                          <strong>{formatSigned(item.unrealized_profit_usd)}</strong>
                          <span>{formatSignedPercent(item.unrealized_return_rate, 2)}</span>
                        </div>
                      </td>
                    </tr>
                  )) : <tr><td colSpan="8" className="emptyTable">{t('workspace.empty')}</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}

        {activeView === 'monitor' ? (
          isMobileViewport ? <DesktopOnlyNotice title={t('monitor.title')} /> : <section className="panel">
            <div className="panelHeader rowHeader">
              <div>
                <span className="panelKicker">System Monitor</span>
                <h2>{t('monitor.title')}</h2>
              </div>
              <button className="ghostButton" onClick={() => { loadHealth(); loadTalkerLearning(); }}>{t('monitor.refresh')}</button>
            </div>
            <div className="monitorHeroGrid">
              <section className="monitorHeroCard monitorStatusCard">
                <span className="panelKicker">Runtime</span>
                <strong>{String(health?.status || 'offline').toUpperCase()}</strong>
                <p>{t('monitor.runtimeDesc')}</p>
                <div className="monitorInlineMeta">
                  <span>{health?.model || t('monitor.waitingBackendShort')}</span>
                  <em>{health?.api_url || '-'}</em>
                </div>
              </section>
              <section className="monitorHeroCard">
                <span className="panelKicker">Portfolio Snapshot</span>
                <strong>{overviewMetrics?.total_assets || displayDashboard.length}</strong>
                <p>{t('monitor.portfolioDesc')}</p>
                <div className="monitorInlineMeta">
                  <span>{t('monitor.pdfCount', { count: selectionSummary?.pdf_files || 0 })}</span>
                  <em>{selectedAssetSummary?.security_name || t('monitor.noAsset')}</em>
                </div>
              </section>
            </div>
            <div className="monitorDetailGrid">
              <section className="monitorPanelCard">
                <div className="panelHeader compactHeader">
                  <span className="panelKicker">Service Matrix</span>
                  <h3>{t('monitor.serviceMatrix')}</h3>
                </div>
                <div className="monitorServiceList">
                  <div className="monitorServiceItem"><span>{t('monitor.apiStatus')}</span><strong>{health?.status || 'offline'}</strong><em>{health?.api_url || '-'}</em></div>
                  <div className="monitorServiceItem"><span>{t('monitor.fastModel')}</span><strong>FSCM-Model-Instruct</strong><em>{t('monitor.fastModelDesc')}</em></div>
                  <div className="monitorServiceItem"><span>{t('monitor.thinkingModel')}</span><strong>FSCM-Model-Thinking</strong><em>{t('monitor.thinkingModelDesc')}</em></div>
                </div>
              </section>
              <section className="monitorPanelCard">
                <div className="panelHeader compactHeader">
                  <span className="panelKicker">Talker</span>
                  <h3>{t('monitor.talkerStatus')}</h3>
                </div>
                <div className="monitorNarrativeBlock">
                  <strong>{t('monitor.talkerConnected')}</strong>
                  <p>{t('monitor.talkerDesc')}</p>
                </div>
                <div className="monitorCapabilityGrid">
                  <div className="monitorCapabilityItem"><span>{t('monitor.dialogMode')}</span><strong>{talkerMode === 'thinking' ? t('monitor.thinkingMode') : t('monitor.fastMode')}</strong></div>
                  <div className="monitorCapabilityItem"><span>{t('monitor.positioning')}</span><strong>{t('monitor.positioningValue')}</strong></div>
                  <div className="monitorCapabilityItem"><span>{t('monitor.outputChain')}</span><strong>{t('monitor.outputChainValue')}</strong></div>
                  <div className="monitorCapabilityItem"><span>{t('monitor.entryPoint')}</span><strong>{t('monitor.entryPointValue')}</strong></div>
                </div>
              </section>
            </div>
            <section className="monitorPanelCard monitorLearningPanel">
              <div className="panelHeader rowHeader">
                <div>
                  <span className="panelKicker">Talker Learning</span>
                  <h3>{t('monitor.learning')}</h3>
                </div>
              </div>
              <div className="monitorLearningStatsGrid">
                <div className="monitorCapabilityItem"><span>{t('monitor.pendingBadCases')}</span><strong>{talkerLearning.open_bad_case_count}</strong></div>
                <div className="monitorCapabilityItem"><span>{t('monitor.activeSamples')}</span><strong>{talkerLearning.active_fewshot_count}</strong></div>
                <div className="monitorCapabilityItem"><span>{t('monitor.semanticCandidates')}</span><strong>{talkerLearning.semantic_candidates.length}</strong></div>
              </div>
              <div className="monitorLearningBoard">
                <div className="monitorLearningLane">
                  <div className="monitorNarrativeBlock">
                    <strong>{t('monitor.semanticCandidates')}</strong>
                    <p>{t('monitor.semanticCandidatesDesc')}</p>
                  </div>
                  <div className="monitorLearningHelpCard">
                    <div className="monitorLearningHelpRow"><strong>{t('monitor.approveRule')}</strong><span>{t('monitor.approveRuleHelp')}</span></div>
                    <div className="monitorLearningHelpRow"><strong>{t('monitor.approveSample')}</strong><span>{t('monitor.approveSampleHelp')}</span></div>
                    <div className="monitorLearningHelpRow"><strong>{t('monitor.ignore')}</strong><span>{t('monitor.ignoreHelp')}</span></div>
                  </div>
                  <div className="monitorLearningList monitorLearningScrollableList">
                    {talkerLearning.semantic_candidates.length ? talkerLearning.semantic_candidates.slice(0, 12).map((item) => (
                      <div key={`semantic-candidate-${item.id}`} className="monitorLearningItem">
                        <strong>{item.summary || item.canonical_name || t('monitor.unnamedCandidate')}</strong>
                        <span>{item.pattern_text || item.recommended_route || t('monitor.undefinedPattern')}</span>
                        <em>{item.details || t('monitor.noCandidateDesc')}</em>
                        <div className="monitorLearningActions">
                          <button className="secondaryButton" onClick={() => submitLearningAction(`/talker/learning/semantic-candidates/${item.id}/approve-rule`, `rule-${item.id}`, t('monitor.approvedRule'))} disabled={reviewActionLoadingKey === `rule-${item.id}`}>{t('monitor.approveRule')}</button>
                          <button className="primaryButton" onClick={() => submitLearningAction(`/talker/learning/semantic-candidates/${item.id}/approve-fewshot`, `fewshot-${item.id}`, t('monitor.approvedFewshot'))} disabled={reviewActionLoadingKey === `fewshot-${item.id}`}>{t('monitor.approveSample')}</button>
                          <button className="ghostButton" onClick={() => submitLearningAction(`/talker/learning/semantic-candidates/${item.id}/ignore`, `ignore-sem-${item.id}`, t('monitor.ignoredSemantic'))} disabled={reviewActionLoadingKey === `ignore-sem-${item.id}`}>{t('monitor.ignore')}</button>
                        </div>
                      </div>
                    )) : <div className="monitorLearningEmpty">{t('monitor.noSemanticCandidates')}</div>}
                  </div>
                </div>
                <div className="monitorLearningLane">
                  <div className="monitorNarrativeBlock">
                    <strong>{t('monitor.recentBadCases')}</strong>
                    <p>{t('monitor.recentBadCasesDesc')}</p>
                  </div>
                  <div className="monitorLearningList monitorLearningScrollableList">
                    {talkerLearning.open_bad_cases.length ? talkerLearning.open_bad_cases.slice(0, 12).map((item) => (
                      <div key={`bad-case-${item.id}`} className="monitorLearningItem">
                        <strong>{item.user_question || t('monitor.noOriginalQuestion')}</strong>
                        <span>{item.failure_type || 'unknown_failure'}</span>
                        <em>{item.failure_reason || item.generated_sql || t('monitor.noFailureDetail')}</em>
                        <div className="monitorLearningActions">
                          <button className="ghostButton" onClick={() => submitLearningAction(`/talker/learning/bad-cases/${item.id}/ignore`, `ignore-bad-${item.id}`, t('monitor.ignoredBadCase'))} disabled={reviewActionLoadingKey === `ignore-bad-${item.id}`}>{t('monitor.ignore')}</button>
                        </div>
                      </div>
                    )) : <div className="monitorLearningEmpty">{t('monitor.noBadCases')}</div>}
                  </div>
                </div>
                <div className="monitorLearningLane">
                  <div className="monitorNarrativeBlock">
                    <strong>{t('monitor.activeFewshots')}</strong>
                    <p>{t('monitor.activeFewshotsDesc')}</p>
                  </div>
                  <div className="monitorLearningList monitorLearningScrollableList">
                    {pagedFewshotReviewItems.length ? pagedFewshotReviewItems.map((item) => (
                      <div key={`fewshot-${item.id}`} className="monitorLearningItem">
                        <strong>{item.question || t('monitor.unnamedSample')}</strong>
                        <span>{Array.isArray(item.tags) && item.tags.length ? item.tags.join(' / ') : t('monitor.untagged')}</span>
                        <em>{item.explanation || item.sql_text || t('monitor.noSampleDesc')}</em>
                      </div>
                    )) : <div className="monitorLearningEmpty">{t('monitor.noActiveSamples')}</div>}
                  </div>
                  {talkerLearning.active_fewshots.length > fewshotReviewPageSize ? (
                    <div className="monitorLearningPagination">
                      <button className="ghostButton" onClick={() => setFewshotReviewPage((current) => Math.max(1, current - 1))} disabled={safeFewshotReviewPage <= 1}>{t('workspace.prevPage')}</button>
                      <span>{t('pdf.page', { page: safeFewshotReviewPage })} / {fewshotReviewTotalPages}</span>
                      <button className="ghostButton" onClick={() => setFewshotReviewPage((current) => Math.min(fewshotReviewTotalPages, current + 1))} disabled={safeFewshotReviewPage >= fewshotReviewTotalPages}>{t('workspace.nextPage')}</button>
                    </div>
                  ) : null}
                </div>
              </div>
            </section>
          </section>
        ) : null}

        {activeView === 'configure' && currentUser?.role === 'admin' ? (
          <ConfigurePanel
            currentUser={currentUser}
            message={message}
            setMessage={setMessage}
            apiFetch={apiFetch}
            isMobileViewport={isMobileViewport}
            DesktopOnlyNotice={DesktopOnlyNotice}
            userManagement={userManagement}
            loadUsers={loadUsers}
            featureSettings={featureSettings}
            updateTalkerFeature={updateTalkerFeature}
            updatePreviewRedactionFeature={updatePreviewRedactionFeature}
            updateDbManagementFeature={updateDbManagementFeature}
            userForm={userForm}
            setUserForm={setUserForm}
            submitCreateUser={submitCreateUser}
            passwordResetDrafts={passwordResetDrafts}
            setPasswordResetDrafts={setPasswordResetDrafts}
            submitResetPassword={submitResetPassword}
            userActionLoadingKey={userActionLoadingKey}
          />
        ) : null}

        {activeView === 'talker' && featureSettings.talker_enabled ? (
          <section className="panel">
            <div className="panelHeader rowHeader">
              <div>
                <span className="panelKicker">Investment-Talker</span>
                <h2>{t('talker.title')}</h2>
              </div>
            </div>
            <div className="talkerConsoleCard">
              <div className="talkerToolbar">
                <div className="talkerModeGroup" role="group" aria-label={t('talker.modeAria')}>
                  <button className={talkerMode === 'fast' ? 'themeOptionButton activeThemeOptionButton' : 'themeOptionButton'} onClick={() => setTalkerMode('fast')}>{t('talker.fastMode')}</button>
                  <button className={talkerMode === 'thinking' ? 'themeOptionButton activeThemeOptionButton' : 'themeOptionButton'} onClick={() => setTalkerMode('thinking')}>{t('talker.thinkingMode')}</button>
                </div>
                <span className="workspaceToolbarHint">{t('talker.hint')}</span>
              </div>
              <div className="talkerMessageList" ref={talkerMessageListRef}>
                {talkerMessages.map((item, index) => (
                  <div key={`talker-message-${index}`} className={item.role === 'user' ? 'talkerMessageBubble userTalkerMessageBubble' : 'talkerMessageBubble assistantTalkerMessageBubble'}>
                    <span>{item.role === 'user' ? t('talker.you') : t('talker.assistant')}</span>
                    <strong>{item.role === 'assistant' ? (item.displayContent ?? item.content) : item.content}</strong>
                    {item.role === 'assistant' && Array.isArray(item.columns) && item.columns.length ? (
                      <div className="talkerResultCard">
                        <div className="talkerResultHeader">
                          <em>{t('talker.queryResult')}</em>
                        </div>
                        {Array.isArray(item.columns) && item.columns.length ? (
                          <div className="darkTableWrap">
                            <div className="tableWrap">
                              <table>
                                <thead>
                                  <tr>
                                    {item.columns.map((column) => (
                                      <th key={`talker-column-${index}-${column}`}>{column}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {(item.rows || []).length ? item.rows.map((row, rowIndex) => (
                                    <tr key={`talker-row-${index}-${rowIndex}`}>
                                      {item.columns.map((column) => (
                                        <td key={`talker-cell-${index}-${rowIndex}-${column}`}>{String(row?.[column] ?? '')}</td>
                                      ))}
                                    </tr>
                                  )) : (
                                    <tr>
                                      <td colSpan={item.columns.length} className="emptyTable">{t('talker.noData')}</td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ))}
                {talkerLoading ? <div className="talkerStreamingHint">{t('talker.thinking')}</div> : null}
              </div>
              <div className="talkerComposer">
                <textarea
                  value={talkerInput}
                  onChange={(event) => setTalkerInput(event.target.value)}
                  onKeyDown={handleTalkerInputKeyDown}
                  placeholder={t('talker.placeholder')}
                />
                <div className="talkerComposerActions">
                  <button className="secondaryButton" onClick={() => setTalkerMessages([{ role: 'assistant', content: t('talker.greeting'), displayContent: t('talker.greeting'), sql: '', columns: [], rows: [], usedSql: false }])} disabled={talkerLoading}>{t('talker.clear')}</button>
                  <button className="primaryButton" onClick={submitTalkerMessage} disabled={talkerLoading || !String(talkerInput || '').trim()}>{talkerLoading ? t('talker.sending') : t('talker.send')}</button>
                </div>
              </div>
            </div>
          </section>
        ) : null}

        {activeView === 'documents' ? (
        isMobileViewport ? <DesktopOnlyNotice title={t('desktopOnly.documents')} description={t('desktopOnly.documentsDesc')} /> : <section className="panel intakeWorkspace">
          <div className="workspaceHeader rowHeader">
            <div>
              <span className="panelKicker">Document Intake Workspace</span>
              <h2>{t('docs.title')}</h2>
              <p className="workspaceDesc">{t('docs.desc')}</p>
            </div>
            <button className="primaryButton" onClick={triggerExtraction} disabled={loading}>
              {extractionTaskId ? t('docs.queuing') : loading ? t('docs.extracting') : t('docs.startExtraction')}
            </button>
          </div>

          {message ? <p className="messageBanner">{message}</p> : null}
          {documentExtractionSummary ? (
            <div className={documentExtractionSummary.failedFiles > 0 || documentExtractionSummary.failedPages > 0 ? 'documentResultBanner warning' : 'documentResultBanner success'}>
              <div className="documentResultBannerHeader">
                <div className="documentResultBannerTitle">
                  <strong>{t('docs.result.title')}</strong>
                  <span>{documentExtractionSummary.failedFiles > 0 || documentExtractionSummary.failedPages > 0 ? t('docs.result.partialFail') : t('docs.result.allDone')}</span>
                </div>
                <button className="ghostButton compactActionButton" onClick={() => setDocumentExtractionSummary(null)}>{t('docs.result.close')}</button>
              </div>
              <div className="documentResultStats">
                <div className="documentResultStat">
                  <span>{t('docs.result.successFiles')}</span>
                  <strong>{documentExtractionSummary.processedFiles}</strong>
                </div>
                <div className="documentResultStat">
                  <span>{t('docs.result.failedFiles')}</span>
                  <strong>{documentExtractionSummary.failedFiles}</strong>
                </div>
                <div className="documentResultStat">
                  <span>{t('docs.result.successPages')}</span>
                  <strong>{documentExtractionSummary.processedPages}</strong>
                </div>
                <div className="documentResultStat">
                  <span>{t('docs.result.failedPages')}</span>
                  <strong>{documentExtractionSummary.failedPages}</strong>
                </div>
                <div className="documentResultStat">
                  <span>{t('docs.result.duplicatePages')}</span>
                  <strong>{documentExtractionSummary.duplicatePages}</strong>
                </div>
              </div>
              <div className="documentResultFileGroups">
                <div className="documentResultFileGroup">
                  <span className="documentResultFileGroupTitle">{t('docs.result.successFiles')}</span>
                  {documentExtractionSummary.successfulFileNames?.length ? (
                    <div className="fileList documentResultFileList successFileList">
                      {documentExtractionSummary.successfulFileNames.map((fileName) => (
                        <span key={`success-${fileName}`} className="fileChip documentResultFileChip successFileChip">{fileName}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="documentResultEmpty">{t('docs.result.noSuccess')}</span>
                  )}
                </div>
                <div className="documentResultFileGroup">
                  <span className="documentResultFileGroupTitle">{t('docs.result.failedFiles')}</span>
                  {documentExtractionSummary.failedFileEntries?.length ? (
                    <div className="documentResultFailureList">
                      {documentExtractionSummary.failedFileEntries.map((entry) => (
                        <div key={`failed-${entry.fileName}`} className="documentResultFailureItem">
                          <strong>{entry.fileName}</strong>
                          <div className="documentResultReasonList">
                            {entry.reasons.map((reason) => (
                              <span key={`${entry.fileName}-${reason}`} className="documentResultReasonText">{reason}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="documentResultEmpty">{t('docs.result.noFailure')}</span>
                  )}
                </div>
                <div className="documentResultFileGroup">
                  <span className="documentResultFileGroupTitle">{t('docs.result.duplicateSkipped')}</span>
                  {documentExtractionSummary.duplicateFileEntries?.length ? (
                    <div className="documentResultFailureList">
                      {documentExtractionSummary.duplicateFileEntries.map((entry) => (
                        <div key={`duplicate-${entry.fileName}`} className="documentResultFailureItem">
                          <strong>{entry.fileName}</strong>
                          <div className="documentResultReasonList">
                            {entry.reasons.map((reason) => (
                              <span key={`${entry.fileName}-${reason}`} className="documentResultReasonText">{reason}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="documentResultEmpty">{t('docs.result.noDuplicate')}</span>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          <DocumentsWorkspace
            selectionSummary={selectionSummary}
            extractionFailures={extractionFailures}
            handleFileChange={handleFileChange}
            documentTree={documentTree}
            expandedYears={expandedYears}
            expandedMonths={expandedMonths}
            toggleYear={toggleYear}
            toggleMonth={toggleMonth}
            previewStoredDocument={previewStoredDocument}
            deleteStoredDocument={deleteStoredDocument}
            selectedFiles={selectedFiles}
            fileGroups={fileGroups}
            extractionItems={extractionItems}
            previewSelectedFile={previewSelectedFile}
            removeSelectedFile={removeSelectedFile}
            redactionStatusByFile={redactionStatusByFile}
            onAnnotateFile={annotateSelectedFile}
            isFileArchived={isFileArchived}
          />
        </section>
        ) : null}

        {activeView === 'analytics' ? (
        <>
        <section className="panel ledgerPanel fullWidthPanel">
            <div className={mobileAnalyticsDetailOpen ? 'analyticsWorkspaceGrid mobileAnalyticsDetailOpen' : 'analyticsWorkspaceGrid'}>
              <div className="panel analyticsAssetRail nestedPanel">
                <div className="panelHeader compactHeader">
                  <span className="panelKicker">Asset Universe</span>
                  <h2>{t('analytics.assetList')}</h2>
                </div>
                <div className="analyticsFilterBar">
                  <input value={assetFilter} onChange={(e) => setAssetFilter(e.target.value)} placeholder={t('analytics.searchPlaceholder')} />
                  <button className="secondaryButton" onClick={() => setAssetFilter('')}>{t('analytics.clear')}</button>
                </div>
                <div className="analyticsPositionFilterRow">
                  {analyticsPositionFilters.map((option) => (
                    <button
                      key={option.key}
                      className={analyticsPositionFilter === option.key ? 'analyticsPositionFilterButton activeAnalyticsPositionFilterButton' : 'analyticsPositionFilterButton'}
                      onClick={() => setAnalyticsPositionFilter(option.key)}
                    >
                      {t(option.labelKey)}
                    </button>
                  ))}
                </div>
                <div className="analyticsPositionStatsGrid">
                  {analyticsSummaryCards.map((item) => (
                    <button
                      key={item.key}
                      className={analyticsPositionFilter === item.key ? 'analyticsPositionStatCard activeAnalyticsPositionStatCard' : 'analyticsPositionStatCard'}
                      onClick={() => setAnalyticsPositionFilter(item.key)}
                    >
                      <span>{t(item.labelKey)}</span>
                      <strong>{item.count}</strong>
                      <em>{item.detail}</em>
                    </button>
                  ))}
                </div>
                <div className="analyticsAssetRailMeta">
                  <span>{t('analytics.railMeta', { label: t(analyticsPositionFilters.find((item) => item.key === analyticsPositionFilter)?.labelKey) || t('position.all'), count: analyticsScopedAssets.length })}</span>
                  <span>{t('analytics.railPage', { page: safeAnalyticsPage, total: analyticsPageCount, count: filteredAssets.length })}</span>
                </div>
                {filteredAssets.length ? (
                  <div className="analyticsPager">
                    <button className="ghostButton compactPagerButton" onClick={() => setAnalyticsPage((current) => Math.max(1, current - 1))} disabled={safeAnalyticsPage <= 1}>{t('workspace.prevPage')}</button>
                    <div className="workspacePageNumberRow">
                      {analyticsPageNumbers.map((pageNumber) => (
                        <button
                          key={`analytics-page-${pageNumber}`}
                          className={pageNumber === safeAnalyticsPage ? 'workspacePageNumberButton activeWorkspacePageNumberButton' : 'workspacePageNumberButton'}
                          onClick={() => setAnalyticsPage(pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      ))}
                    </div>
                    <button className="ghostButton compactPagerButton" onClick={() => setAnalyticsPage((current) => Math.min(analyticsPageCount, current + 1))} disabled={safeAnalyticsPage >= analyticsPageCount}>{t('workspace.nextPage')}</button>
                  </div>
                ) : null}
                <div className="analyticsAssetList">
                  {pagedAnalyticsAssets.length ? pagedAnalyticsAssets.map((item) => {
                    const assetKeyword = item.asset_no;
                    const isActive = item.asset_no === keyword;
                    const positionStatus = getAssetPositionStatus(item);
                    return (
                      <button key={item.asset_no} className={isActive ? 'analyticsAssetItem activeAnalyticsAsset' : 'analyticsAssetItem'} onClick={() => selectAsset(assetKeyword)} title={`${item.security_name} (${item.asset_no})`}>
                        <div className="analyticsAssetPrimaryRow">
                          <strong>{item.security_name}</strong>
                          <em className={Number(item.total_return_rate) >= 0 ? 'positive' : 'negative'}>{t('analytics.realizedPercent', { value: formatNumber(item.total_return_rate, 2) })}</em>
                        </div>
                        <div className="analyticsAssetSecondaryRow">
                          <span>{item.asset_no}</span>
                          <span>{item.currency}</span>
                        </div>
                        <div className="analyticsAssetStatusRow">
                          <span className={positionStatus === 'CURRENT' ? 'analyticsAssetStatusTag currentStatusTag' : positionStatus === 'EXITED' ? 'analyticsAssetStatusTag exitedStatusTag' : 'analyticsAssetStatusTag inactiveStatusTag'}>{getAssetPositionStatusLabel(positionStatus, t)}</span>
                          <span>{t('analytics.holding', { value: formatNumber(item.current_quantity, 3) })}</span>
                        </div>
                      </button>
                    );
                  }) : <div className="emptyState">{t('analytics.noMatch')}</div>}
                </div>
              </div>

              <div className="analyticsDetailColumn">
                <button type="button" className="ghostButton mobileOnly mobileBackButton" onClick={() => setMobileAnalyticsDetailOpen(false)}>{t('analytics.backToList')}</button>
                <div className="panelHeader rowHeader">
                  <div>
                    <span className="panelKicker">Asset Lifecycle Ledger</span>
                    <h2>{t('analytics.lifecycleTitle', { name: lifecycleMetrics?.security_name || selectedAssetSummary?.security_name || keyword })}</h2>
                  </div>
                  <div className="topbarActions">
                    {lifecycleMetrics && !isMobileViewport ? (
                      assetEditMode ? (
                        <>
                          <button className="secondaryButton compactActionButton" onClick={saveAssetEdit} disabled={assetEditSaving}>{assetEditSaving ? t('analytics.saving') : t('analytics.saveMaster')}</button>
                          <button className="ghostButton compactActionButton" onClick={() => setAssetEditMode(false)} disabled={assetEditSaving}>{t('analytics.cancel')}</button>
                        </>
                      ) : (
                        <button className="ghostButton compactActionButton" onClick={beginAssetEdit}>{t('analytics.editMaster')}</button>
                      )
                    ) : null}
                    <span className="pillTag">{t('analytics.eventCount', { count: displayLedger.length })}</span>
                  </div>
                </div>
                {lifecycleMetrics ? (
                  <>
                    <div className="mobileOnly analyticsHeadlineCard">
                      <div className="analyticsHeadlineTop">
                        <span className={`analyticsAssetStatusTag ${getAssetPositionStatus(lifecycleMetrics) === 'CURRENT' ? 'currentStatusTag' : getAssetPositionStatus(lifecycleMetrics) === 'EXITED' ? 'exitedStatusTag' : 'inactiveStatusTag'}`}>{getAssetPositionStatusLabel(getAssetPositionStatus(lifecycleMetrics), t)}</span>
                        <span className="analyticsHeadlinePriceMeta">{t('analytics.latestPrice', { price: displayedLifecyclePrice ? formatNumber(displayedLifecyclePrice, 4) : '-', date: displayedLifecyclePriceDate ? ` · ${displayedLifecyclePriceDate}` : '' })}</span>
                      </div>
                      <div className="analyticsHeadlineGrid">
                        <div className="analyticsHeadlineStat">
                          <span>{t('analytics.unrealizedProfit')}</span>
                          <strong className={Number(displayedUnrealizedProfit) >= 0 ? 'positive' : 'negative'}>{formatSigned(displayedUnrealizedProfit)}</strong>
                          <em className={unrealizedReturnRate === null ? '' : Number(unrealizedReturnRate) >= 0 ? 'positive' : 'negative'}>{unrealizedReturnRate === null ? '-' : formatSignedPercent(unrealizedReturnRate)}</em>
                        </div>
                        <div className="analyticsHeadlineStat">
                          <span>{t('analytics.realizedProfit')}</span>
                          <strong className={Number(lifecycleMetrics.realized_profit) >= 0 ? 'positive' : 'negative'}>{formatSigned(lifecycleMetrics.realized_profit)}</strong>
                          <em className={Number(lifecycleMetrics.total_return_rate) >= 0 ? 'positive' : 'negative'}>{formatSignedPercent(lifecycleMetrics.total_return_rate)}</em>
                        </div>
                        <div className="analyticsHeadlineStat">
                          <span>{t('analytics.currentHolding')}</span>
                          <strong>{formatNumber(lifecycleMetrics.current_quantity, 3)}</strong>
                          <em>{t('analytics.avgCost', { value: formatNumber(lifecycleMetrics.average_cost, 4) })}</em>
                        </div>
                        <div className="analyticsHeadlineStat">
                          <span>{hasLifecycleFinancingCost ? t('analytics.dividendWithCost') : t('analytics.dividend')}</span>
                          <strong className={Number(lifecycleMetrics.total_dividend) >= 0 ? 'positive' : 'negative'}>{formatSigned(lifecycleMetrics.total_dividend)}</strong>
                          {hasLifecycleFinancingCost ? <em className="negative">{t('analytics.interest', { value: lifecycleFinancingCostLabel })}</em> : null}
                        </div>
                      </div>
                      <details className="analyticsAssetInfoDisclosure">
                        <summary>{t('analytics.assetInfo')}</summary>
                        <div className="dataRow"><span>{t('analytics.assetCode')}</span><strong>{lifecycleMetrics.asset_no}</strong></div>
                        <div className="dataRow"><span>{t('analytics.stockTicker')}</span><strong>{lifecycleMetrics.stock_ticker || '-'}</strong></div>
                        <div className="dataRow"><span>{t('analytics.assetName')}</span><strong>{lifecycleMetrics.security_name}</strong></div>
                        <div className="dataRow"><span>{t('analytics.currencyType')}</span><strong>{lifecycleMetrics.currency} / {lifecycleMetrics.asset_type}</strong></div>
                        <div className="dataRow"><span>{t('analytics.firstBuyDate')}</span><strong>{lifecycleMetrics.first_buy_date || '-'}</strong></div>
                        <div className="dataRow"><span>{t('analytics.firstHoldingDate')}</span><strong>{lifecycleMetrics.first_holding_date || '-'}</strong></div>
                        <div className="dataRow"><span>{t('analytics.dividendDate')}</span><strong>{lifecycleMetrics.dividend_date || '-'}</strong></div>
                        <div className="dataRow"><span>{t('analytics.totalBuy')}</span><strong>{formatNumber(lifecycleMetrics.total_buy_quantity, 3)}</strong></div>
                        <div className="dataRow"><span>{t('analytics.totalSell')}</span><strong>{formatNumber(lifecycleMetrics.total_sell_quantity, 3)}</strong></div>
                      </details>
                    </div>

                    <div className="desktopOnly analyticsHeadlineStatsRow">
                      <section className="metricCard accentBlue analyticsHeadlineMetricCard">
                        <span>{t('analytics.latestPriceShort')}</span>
                        <strong>{displayedLifecyclePrice ? formatNumber(displayedLifecyclePrice, 4) : '-'}</strong>
                        <em>{displayedLifecyclePriceDate ? t('analytics.priceDate', { date: displayedLifecyclePriceDate }) : t('analytics.noPriceDate')}</em>
                      </section>
                      <section className="metricCard accentGreen analyticsHeadlineMetricCard">
                        <span>{t('analytics.unrealizedProfit')}</span>
                        <strong className={Number(displayedUnrealizedProfit) >= 0 ? 'positive' : 'negative'}>{formatSigned(displayedUnrealizedProfit)}</strong>
                        <em className={unrealizedReturnRate === null ? '' : Number(unrealizedReturnRate) >= 0 ? 'positive' : 'negative'}>{unrealizedReturnRate === null ? '-' : formatSignedPercent(unrealizedReturnRate)}</em>
                      </section>
                      <section className="metricCard accentOrange analyticsHeadlineMetricCard">
                        <span>{t('analytics.realizedProfit')}</span>
                        <strong className={Number(lifecycleMetrics.realized_profit) >= 0 ? 'positive' : 'negative'}>{formatSigned(lifecycleMetrics.realized_profit)}</strong>
                        <em className={Number(lifecycleMetrics.total_return_rate) >= 0 ? 'positive' : 'negative'}>{formatSignedPercent(lifecycleMetrics.total_return_rate)}</em>
                      </section>
                      <section className="metricCard accentRose analyticsHeadlineMetricCard">
                        <span>{t('analytics.currentHolding')}</span>
                        <strong>{formatNumber(lifecycleMetrics.current_quantity, 3)}</strong>
                        <em>{t('analytics.avgCost', { value: formatNumber(lifecycleMetrics.average_cost, 4) })}</em>
                      </section>
                      <section className="metricCard accentBlue analyticsHeadlineMetricCard">
                        <span>{hasLifecycleFinancingCost ? t('analytics.dividendWithCost') : t('analytics.dividend')}</span>
                        <strong className={Number(lifecycleMetrics.total_dividend) >= 0 ? 'positive' : 'negative'}>{formatSigned(lifecycleMetrics.total_dividend)}</strong>
                        {hasLifecycleFinancingCost ? <em className="negative">{t('analytics.loanInterest', { value: lifecycleFinancingCostLabel })}</em> : <em>&nbsp;</em>}
                      </section>
                    </div>

                    <div className="desktopOnly analyticsAssetInfoPanel">
                      <div className="panelHeader compactHeader">
                        <span className="panelKicker">Asset Info</span>
                        <h2>{t('analytics.assetInfo')}</h2>
                      </div>
                      <div className="analyticsAssetInfoGrid">
                        <div className="dataRow editableDataRow"><span>{t('analytics.assetCode')}</span>{assetEditMode ? <input value={assetEditForm.asset_no} onChange={(event) => setAssetEditForm((current) => ({ ...current, asset_no: event.target.value }))} /> : <strong>{lifecycleMetrics.asset_no}</strong>}</div>
                        <div className="dataRow editableDataRow"><span>{t('analytics.stockTicker')}</span>{assetEditMode ? <input value={assetEditForm.stock_ticker} onChange={(event) => setAssetEditForm((current) => ({ ...current, stock_ticker: event.target.value }))} /> : <strong>{lifecycleMetrics.stock_ticker || '-'}</strong>}</div>
                        <div className="dataRow editableDataRow"><span>{t('analytics.assetName')}</span>{assetEditMode ? <input value={assetEditForm.security_name} onChange={(event) => setAssetEditForm((current) => ({ ...current, security_name: event.target.value }))} /> : <strong>{lifecycleMetrics.security_name}</strong>}</div>
                        <div className="dataRow editableDataRow"><span>{t('analytics.currencyType')}</span>{assetEditMode ? <div className="assetMetaEditPair"><input value={assetEditForm.currency} onChange={(event) => setAssetEditForm((current) => ({ ...current, currency: event.target.value }))} /><input value={assetEditForm.asset_type} onChange={(event) => setAssetEditForm((current) => ({ ...current, asset_type: event.target.value }))} /></div> : <strong>{lifecycleMetrics.currency} / {lifecycleMetrics.asset_type}</strong>}</div>
                        <div className="dataRow"><span>{t('analytics.firstBuyDate')}</span><strong>{lifecycleMetrics.first_buy_date || '-'}</strong></div>
                        <div className="dataRow"><span>{t('analytics.firstHoldingDate')}</span><strong>{lifecycleMetrics.first_holding_date || '-'}</strong></div>
                        <div className="dataRow"><span>{t('analytics.dividendDate')}</span><strong>{lifecycleMetrics.dividend_date || '-'}</strong></div>
                        <div className="dataRow"><span>{t('analytics.totalBuy')}</span><strong>{formatNumber(lifecycleMetrics.total_buy_quantity, 3)}</strong></div>
                        <div className="dataRow"><span>{t('analytics.totalSell')}</span><strong>{formatNumber(lifecycleMetrics.total_sell_quantity, 3)}</strong></div>
                      </div>
                    </div>

                    <div className="analyticsPanelSwitchRow">
                      {linkedEvents.length ? (
                        <button
                          className={selectedAnalyticsPanel === 'LINKED' ? 'analyticsPanelSwitchCard activeAnalyticsPanelSwitchCard' : 'analyticsPanelSwitchCard'}
                          onClick={() => setSelectedAnalyticsPanel('LINKED')}
                        >
                          <span className="panelKicker">Financing Chain</span>
                          <strong>{t('panel.linked.title')}</strong>
                          <span>{t('panel.linked.count', { count: linkedEvents.length })}</span>
                        </button>
                      ) : null}
                      <button
                        className={selectedAnalyticsPanel === 'LIFECYCLE' ? 'analyticsPanelSwitchCard activeAnalyticsPanelSwitchCard' : 'analyticsPanelSwitchCard'}
                        onClick={() => setSelectedAnalyticsPanel('LIFECYCLE')}
                      >
                        <span className="panelKicker">Lifecycle Events</span>
                        <strong>{t('panel.lifecycle.title')}</strong>
                        <span>{t('panel.lifecycle.count', { count: displayLedger.length })}</span>
                      </button>
                      {lifecycle?.structured_product_terms ? (
                        <button
                          className={selectedAnalyticsPanel === 'TERMS' ? 'analyticsPanelSwitchCard activeAnalyticsPanelSwitchCard' : 'analyticsPanelSwitchCard'}
                          onClick={() => setSelectedAnalyticsPanel('TERMS')}
                        >
                          <span className="panelKicker">Term Sheet</span>
                          <strong>{t('panel.terms.title')}</strong>
                          <span>{t('panel.terms.count', { count: lifecycle.structured_product_terms.pages.length })}</span>
                        </button>
                      ) : null}
                    </div>

                    {selectedAnalyticsPanel === 'LINKED' && linkedEvents.length ? (
                      <div className="panel nestedPanel analyticsSubPanel linkedEventsPanel">
                        <div className="panelHeader compactHeader rowHeader">
                          <div>
                            <span className="panelKicker">Financing Chain</span>
                            <h2>{t('panel.linked.title')}</h2>
                          </div>
                          <span className="pillTag">{t('panel.linked.count', { count: linkedEvents.length })}</span>
                        </div>
                        <div className="linkedEventSwitchRow">
                          {linkedEventTabs.map((item) => (
                            <button
                              key={item.key}
                              className={item.key === selectedLinkedEventKey ? 'linkedEventSwitchCard activeLinkedEventSwitchCard' : 'linkedEventSwitchCard'}
                              onClick={() => setSelectedLinkedEventKey(item.key)}
                            >
                              <span className={`typeTag ${String(item.eventType).toLowerCase()}`}>{t(item.titleKey)}</span>
                              <strong>{item.subtitle}</strong>
                              <span>{t('panel.linked.itemCount', { count: item.count })}</span>
                            </button>
                          ))}
                        </div>
                        <div className="mobileOnly linkedEventCards">
                          {linkedEventRows.length ? (
                            <div className="analyticsPager lifecyclePager">
                              <button className="ghostButton compactPagerButton" onClick={() => setLinkedEventPage((current) => Math.max(1, current - 1))} disabled={safeLinkedEventPage <= 1}>{t('workspace.prevPage')}</button>
                              <div className="workspacePageNumberRow">
                                {linkedEventPageNumbers.map((pageNumber) => (
                                  <button
                                    key={`linked-event-page-mobile-${pageNumber}`}
                                    className={pageNumber === safeLinkedEventPage ? 'workspacePageNumberButton activeWorkspacePageNumberButton' : 'workspacePageNumberButton'}
                                    onClick={() => setLinkedEventPage(pageNumber)}
                                  >
                                    {pageNumber}
                                  </button>
                                ))}
                              </div>
                              <button className="ghostButton compactPagerButton" onClick={() => setLinkedEventPage((current) => Math.min(linkedEventPageCount, current + 1))} disabled={safeLinkedEventPage >= linkedEventPageCount}>{t('workspace.nextPage')}</button>
                            </div>
                          ) : null}
                          {pagedLinkedEventRows.length ? pagedLinkedEventRows.map((item, index) => (
                            <div key={`mobile-linked-${item.event_type}-${item.reference_no || index}`} className="linkedEventCard">
                              <div className="linkedEventCardHeader">
                                <span className={`typeTag ${String(item.event_type).toLowerCase()}`}>{getLinkedEventTypeLabel(item.event_type, t)}</span>
                                <span>{item.event_date || '-'}</span>
                              </div>
                              <div className="linkedEventCardGrid">
                                <span>{t('panel.linked.amount')}</span><strong>{`${item.currency || '-'} ${formatNumber(item.amount, 2)}`}</strong>
                                <span>{t('panel.linked.interestOrConversion')}</span><strong>{getLinkedEventSecondaryText(item, t)}</strong>
                                <span>{t('panel.linked.startSettleDate')}</span><strong>{item.end_date || '-'}</strong>
                                <span>{t('panel.linked.rateOrFx')}</span><strong>{item.rate ? `${formatNumber(item.rate, item.event_type === 'FINANCING' ? 4 : 6)}${item.event_type === 'FINANCING' ? '%' : ''}` : '-'}</strong>
                              </div>
                              {getLinkedEventNoteText(item, t) !== '-' ? <p className="linkedEventCardNote">{getLinkedEventNoteText(item, t)}</p> : null}
                              {item.source_document_id ? (
                                <button className="ghostButton compactActionButton linkedEventCardPdfButton" onClick={() => previewStoredDocument(item.source_document_id, item.source_file || t('panel.linked.viewPdf'), item.source_page || null)}>
                                  {t('panel.linked.viewPdf')}{item.source_page ? ` P${item.source_page}` : ''}
                                </button>
                              ) : null}
                            </div>
                          )) : <div className="emptyState">{t('panel.linked.empty')}</div>}
                        </div>
                        <div className="desktopOnly tableWrap darkTableWrap linkedEventsTableWrap">
                          {linkedEventRows.length ? (
                            <div className="analyticsPager lifecyclePager">
                              <button className="ghostButton compactPagerButton" onClick={() => setLinkedEventPage((current) => Math.max(1, current - 1))} disabled={safeLinkedEventPage <= 1}>{t('workspace.prevPage')}</button>
                              <div className="workspacePageNumberRow">
                                {linkedEventPageNumbers.map((pageNumber) => (
                                  <button
                                    key={`linked-event-page-${pageNumber}`}
                                    className={pageNumber === safeLinkedEventPage ? 'workspacePageNumberButton activeWorkspacePageNumberButton' : 'workspacePageNumberButton'}
                                    onClick={() => setLinkedEventPage(pageNumber)}
                                  >
                                    {pageNumber}
                                  </button>
                                ))}
                              </div>
                              <button className="ghostButton compactPagerButton" onClick={() => setLinkedEventPage((current) => Math.min(linkedEventPageCount, current + 1))} disabled={safeLinkedEventPage >= linkedEventPageCount}>{t('workspace.nextPage')}</button>
                            </div>
                          ) : null}
                          <table>
                            <thead>
                              <tr>
                                <th>{t('panel.linked.businessDate')}</th>
                                <th>{t('panel.linked.type')}</th>
                                <th>{t('panel.linked.amount')}</th>
                                <th>{t('panel.linked.interestOrConversion')}</th>
                                <th>{t('panel.linked.startSettleDate')}</th>
                                <th>{t('panel.linked.rateOrFx')}</th>
                                <th>{t('panel.linked.note')}</th>
                                {!isMobileViewport ? <th>{t('panel.linked.manualLink')}</th> : null}
                                <th>{t('panel.linked.pdf')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pagedLinkedEventRows.length ? pagedLinkedEventRows.map((item, index) => (
                                <tr key={`${item.event_type}-${item.reference_no || index}`}>
                                  <td>{item.event_date || '-'}</td>
                                  <td>
                                    <span className={`typeTag ${String(item.event_type).toLowerCase()}`}>{getLinkedEventTypeLabel(item.event_type, t)}</span>
                                    {item.reference_no ? <div>{item.reference_no}</div> : null}
                                  </td>
                                  <td>{`${item.currency || '-'} ${formatNumber(item.amount, 2)}`}</td>
                                  <td>{getLinkedEventSecondaryText(item, t)}</td>
                                  <td>{item.end_date || '-'}</td>
                                  <td>{item.rate ? `${formatNumber(item.rate, item.event_type === 'FINANCING' ? 4 : 6)}${item.event_type === 'FINANCING' ? '%' : ''}` : '-'}</td>
                                  <td>{getLinkedEventNoteText(item, t)}</td>
                                  {!isMobileViewport ? (
                                    <td>
                                      {item.event_type === 'FINANCING' && item.reference_no ? (
                                        <div className="linkedEventRelinkCell">
                                          <select
                                            className="linkedEventRelinkSelect"
                                            value={manualLinkedAssets[item.reference_no] || ''}
                                            onChange={(event) => updateManualLinkedAsset(item.reference_no, event.target.value)}
                                            disabled={savingLinkedReferenceNo === item.reference_no}
                                          >
                                            <option value="">{t('panel.linked.selectAsset')}</option>
                                            {assetSelectionOptions.map((option) => (
                                              <option key={`relink-${item.reference_no}-${option.asset_no}`} value={option.asset_no}>{option.label}</option>
                                            ))}
                                          </select>
                                          <button
                                            className="ghostButton compactActionButton"
                                            onClick={() => saveFinancingRelink(item.reference_no)}
                                            disabled={!manualLinkedAssets[item.reference_no] || savingLinkedReferenceNo === item.reference_no}
                                          >
                                            {savingLinkedReferenceNo === item.reference_no ? t('panel.linked.saving') : t('panel.linked.saveLink')}
                                          </button>
                                        </div>
                                      ) : '-'}
                                    </td>
                                  ) : null}
                                  <td>{item.source_document_id ? <button className="ghostButton compactActionButton" onClick={() => previewStoredDocument(item.source_document_id, item.source_file || t('panel.linked.viewPdf'), item.source_page || null)}>{t('panel.linked.view')}{item.source_page ? ` P${item.source_page}` : ''}</button> : '-'}</td>
                                </tr>
                              )) : <tr><td colSpan={isMobileViewport ? 8 : 9} className="emptyTable">{t('panel.linked.empty')}</td></tr>}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : null}

                    {selectedAnalyticsPanel === 'LIFECYCLE' ? (
                      <div className="panel nestedPanel analyticsSubPanel">
                        <div className="panelHeader compactHeader">
                          <span className="panelKicker">Lifecycle Events</span>
                          <h2>{t('panel.lifecycle.title')}</h2>
                        </div>
                        {displayLedger.length ? (
                          <div className="analyticsPager lifecyclePager">
                            <button className="ghostButton compactPagerButton" onClick={() => setLedgerPage((current) => Math.max(1, current - 1))} disabled={safeLedgerPage <= 1}>{t('workspace.prevPage')}</button>
                            <div className="workspacePageNumberRow">
                              {ledgerPageNumbers.map((pageNumber) => (
                                <button
                                  key={`ledger-page-${pageNumber}`}
                                  className={pageNumber === safeLedgerPage ? 'workspacePageNumberButton activeWorkspacePageNumberButton' : 'workspacePageNumberButton'}
                                  onClick={() => setLedgerPage(pageNumber)}
                                >
                                  {pageNumber}
                                </button>
                              ))}
                            </div>
                            <button className="ghostButton compactPagerButton" onClick={() => setLedgerPage((current) => Math.min(ledgerPageCount, current + 1))} disabled={safeLedgerPage >= ledgerPageCount}>{t('workspace.nextPage')}</button>
                          </div>
                        ) : null}
                        <div className="mobileOnly lifecycleEventCards">
                          {pagedLedger.length ? pagedLedger.map((item, index) => (
                            <div key={`mobile-ledger-${item.date}-${index}`} className="lifecycleEventCard">
                              <div className="lifecycleEventCardHeader">
                                <span className={`typeTag ${String(item.type).toLowerCase()}`}>{getLedgerTypeLabel(item.type, t)}</span>
                                <span>{item.date}</span>
                              </div>
                              {item.type === 'CONVERSION_OUT' && (item.related_asset_no || item.related_security_name) ? <p className="lifecycleEventCardNote">{`→ ${item.related_security_name || item.related_asset_no}${item.related_asset_no && item.related_security_name ? ` (${item.related_asset_no})` : ''}`}</p> : null}
                              {item.type === 'CONVERSION_IN' && (item.related_asset_no || item.related_security_name) ? <p className="lifecycleEventCardNote">{`← ${item.related_security_name || item.related_asset_no}${item.related_asset_no && item.related_security_name ? ` (${item.related_asset_no})` : ''}`}</p> : null}
                              <div className="lifecycleEventCardGrid">
                                <span>{t('panel.lifecycle.quantity')}</span><strong>{item.quantity ?? '-'}</strong>
                                <span>{t('panel.lifecycle.price')}</span><strong>{item.price ?? '-'}</strong>
                                <span>{t('panel.linked.amount')}</span><strong className={Number(item.net_amount) >= 0 ? 'positive' : 'negative'}>{formatSigned(item.net_amount)}</strong>
                                <span>{t('panel.lifecycle.runningHolding')}</span><strong>{formatNumber(item.running_quantity, 0)}</strong>
                              </div>
                              {item.source_document_id ? (
                                <button className="ghostButton compactActionButton lifecycleEventCardPdfButton" onClick={() => previewStoredDocument(item.source_document_id, item.source_file || t('panel.linked.viewPdf'), item.source_page || null)}>
                                  {t('panel.linked.viewPdf')}{item.source_page ? ` P${item.source_page}` : ''}
                                </button>
                              ) : null}
                            </div>
                          )) : <div className="emptyState">{t('panel.lifecycle.empty')}</div>}
                        </div>
                        <div className="desktopOnly tableWrap darkTableWrap">
                          <table>
                            <thead>
                              <tr>
                                <th>{t('panel.linked.businessDate')}</th>
                                <th>{t('panel.linked.type')}</th>
                                <th>{t('panel.lifecycle.quantity')}</th>
                                <th>{t('panel.lifecycle.price')}</th>
                                <th>{t('panel.linked.amount')}</th>
                                <th>{t('panel.lifecycle.runningHolding')}</th>
                                <th>{t('panel.linked.pdf')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pagedLedger.length ? pagedLedger.map((item, index) => (
                                <tr key={`${item.date}-${index}`}>
                                  <td>{item.date}</td>
                                  <td>
                                    <span className={`typeTag ${String(item.type).toLowerCase()}`}>{getLedgerTypeLabel(item.type, t)}</span>
                                    {item.type === 'CONVERSION_OUT' && (item.related_asset_no || item.related_security_name) ? <div>{`→ ${item.related_security_name || item.related_asset_no}${item.related_asset_no && item.related_security_name ? ` (${item.related_asset_no})` : item.related_asset_no ? ` (${item.related_asset_no})` : ''}`}</div> : null}
                                    {item.type === 'CONVERSION_IN' && (item.related_asset_no || item.related_security_name) ? <div>{`← ${item.related_security_name || item.related_asset_no}${item.related_asset_no && item.related_security_name ? ` (${item.related_asset_no})` : item.related_asset_no ? ` (${item.related_asset_no})` : ''}`}</div> : null}
                                  </td>
                                  <td>{item.quantity ?? '-'}</td>
                                  <td>{item.price ?? '-'}</td>
                                  <td className={Number(item.net_amount) >= 0 ? 'positive' : 'negative'}>{formatSigned(item.net_amount)}</td>
                                  <td>{formatNumber(item.running_quantity, 0)}</td>
                                  <td>{item.source_document_id ? <button className="ghostButton compactActionButton" onClick={() => previewStoredDocument(item.source_document_id, item.source_file || t('panel.linked.viewPdf'), item.source_page || null)}>{t('panel.linked.view')}{item.source_page ? ` P${item.source_page}` : ''}</button> : '-'}</td>
                                </tr>
                              )) : <tr><td colSpan="7" className="emptyTable">{t('panel.lifecycle.empty')}</td></tr>}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : null}

                    {selectedAnalyticsPanel === 'TERMS' && lifecycle?.structured_product_terms ? (
                      <>
                      <StructuredProductDashboard
                        terms={lifecycle.structured_product_terms}
                        metrics={lifecycleMetrics}
                        t={t}
                        onPreviewPdf={previewStoredDocument}
                      />
                      <div className="panel nestedPanel analyticsSubPanel termSheetLegacy">
                        <div className="panelHeader compactHeader">
                          <span className="panelKicker">Term Sheet</span>
                          <h2>{t('panel.terms.title')}</h2>
                        </div>
                        <div className="termSheetSummaryCard">
                          <div className="termSheetSummaryGrid">
                            <div className="termSheetSummaryItem">
                              <span>{t('panel.terms.productName')}</span>
                              <strong>{lifecycle.structured_product_terms.product_name || '-'}</strong>
                            </div>
                            <div className="termSheetSummaryItem">
                              <span>Tranche Code</span>
                              <strong className="termSheetTrancheCode">{lifecycle.structured_product_terms.tranche_code || '-'}</strong>
                            </div>
                            <div className="termSheetSummaryItem">
                              <span>Trade Date</span>
                              <strong>{lifecycle.structured_product_terms.trade_date || '-'}</strong>
                            </div>
                            <div className="termSheetSummaryItem">
                              <span>Start Date</span>
                              <strong>{lifecycle.structured_product_terms.start_date || '-'}</strong>
                            </div>
                            <div className="termSheetSummaryItem">
                              <span>Maturity Date</span>
                              <strong>{lifecycle.structured_product_terms.maturity_date || '-'}</strong>
                            </div>
                            <div className="termSheetSummaryItem">
                              <span>Fixed Coupon Rate</span>
                              <strong>{lifecycle.structured_product_terms.fixed_coupon_rate ? `${(lifecycle.structured_product_terms.fixed_coupon_rate * 100).toFixed(4)}%` : '-'}</strong>
                            </div>
                          </div>
                        </div>
                        {lifecycle.structured_product_terms.pages.length ? (
                          <div className="termSheetPages">
                            {lifecycle.structured_product_terms.pages.map((page) => {
                              const parsedSections = parseTermSheetPage(page.raw_text);
                              return (
                                <details key={`term-page-${page.source_page}`} className="termSheetPageDisclosure">
                                  <summary>
                                    <span className="termSheetPageBadge">P{page.source_page}</span>
                                    <span className="termSheetPageLabel">{t('panel.terms.pageLabel', { page: page.source_page, total: lifecycle.structured_product_terms.pages.length })}</span>
                                    <span className="termSheetPageSectionCount">{t('panel.terms.sectionCount', { count: parsedSections.length })}</span>
                                  </summary>
                                  <div className="termSheetPageContent">
                                    {parsedSections.length ? parsedSections.map((section, sIdx) => {
                                      if (section.type === 'kv') {
                                        return (
                                          <div key={`s-${sIdx}`} className="termSheetKVGrid">
                                            {section.items.map((item, kIdx) => (
                                              <div key={`kv-${kIdx}`} className="termSheetKVItem">
                                                <span>{item.key}</span>
                                                <strong>{item.value || '-'}</strong>
                                              </div>
                                            ))}
                                          </div>
                                        );
                                      }
                                      if (section.type === 'table') {
                                        return (
                                          <div key={`s-${sIdx}`} className="termSheetTableWrapper">
                                            {section.title ? <div className="termSheetTableTitle">{section.title}</div> : null}
                                            <div className="termSheetTableScroll">
                                              <table className="termSheetTable">
                                                <thead>
                                                  <tr>
                                                    {section.headers.map((h, hIdx) => (
                                                      <th key={`th-${hIdx}`}>{h}</th>
                                                    ))}
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {section.rows.map((row, rIdx) => (
                                                    <tr key={`tr-${rIdx}`}>
                                                      {row.map((cell, cIdx) => (
                                                        <td key={`td-${cIdx}`}>{cell}</td>
                                                      ))}
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </div>
                                          </div>
                                        );
                                      }
                                      if (section.type === 'title') {
                                        return (
                                          <div key={`s-${sIdx}`} className="termSheetSectionTitle">{section.text}</div>
                                        );
                                      }
                                      // text block
                                      return (
                                        <div key={`s-${sIdx}`} className="termSheetTextBlock">
                                          {section.lines.map((line, lIdx) => (
                                            <p key={`l-${lIdx}`}>{line}</p>
                                          ))}
                                        </div>
                                      );
                                    }) : <div className="emptyState">{t('panel.terms.noText')}</div>}
                                  </div>
                                </details>
                              );
                            })}
                          </div>
                        ) : <div className="emptyState">{t('panel.terms.noPdf')}</div>}
                      </div>
                      </>
                    ) : null}
                  </>
                ) : (
                  <div className="emptyState">{t('analytics.selectAsset')}</div>
                )}
              </div>
            </div>
        </section>
        </>
        ) : null}

        {pdfPreview.open && pdfPreview.url ? (
          <div className="pdfModalOverlay" onClick={closePdfPreview}>
            <div className="pdfModalPanel" onClick={(event) => event.stopPropagation()}>
              <div className="pdfModalHeader">
                <div className="pdfViewerMeta">
                  <strong>{pdfPreview.title}</strong>
                  <span>{pdfPreview.page ? t('pdf.sourcePage', { page: pdfPreview.page }) : t('pdf.preview')}</span>
                </div>
                <div className="pdfModalActions">
                  {pdfPreview.pageImageUrl && !pdfRedactionMode ? (
                    <div className="pdfZoomControls" role="group" aria-label={t('pdf.zoomAria')}>
                      <button className="ghostButton pdfZoomButton" onClick={() => setPdfPreviewScale((value) => Math.max(0.7, Number((value - 0.15).toFixed(2))))} disabled={pdfPreviewScale <= 0.7}>{t('pdf.zoomOut')}</button>
                      <button className="ghostButton pdfZoomValue" onClick={() => setPdfPreviewScale(1)} title={t('pdf.zoomReset')}>{`${Math.round(pdfPreviewScale * 100)}%`}</button>
                      <button className="ghostButton pdfZoomButton" onClick={() => setPdfPreviewScale((value) => Math.min(2.5, Number((value + 0.15).toFixed(2))))} disabled={pdfPreviewScale >= 2.5}>{t('pdf.zoomIn')}</button>
                    </div>
                  ) : null}
                  {pdfPreview.documentId ? (
                    <button className="ghostButton" onClick={pdfRedactionMode ? closePdfRedactionMode : openPdfRedactionMode} disabled={pdfRedactionLoading}>
                      {pdfRedactionLoading ? t('pdf.loading') : pdfRedactionMode ? t('pdf.backToPreview') : t('pdf.annotateRedaction')}
                    </button>
                  ) : null}
                  {pdfRedactionPreviewOnly && pdfPreview.pageImageUrl ? (
                    <button className="ghostButton" onClick={() => { setPdfRedactionPreviewOnly(false); setPdfRedactionSuggestions(null); revokeObjectUrl(pdfPreview.pageImageUrl); setPdfPreview((current) => ({ ...current, pageImageUrl: '' })); }}>{t('pdf.viewOriginal')}</button>
                  ) : pdfPreview.documentId && !pdfRedactionMode ? (
                    <button className="ghostButton" onClick={() => loadRedactionPreviewOverlay(pdfPreview.documentId, pdfPreview.fileName || pdfPreview.title || '', Number(pdfPreview.page || 1))}>{t('pdf.redactionPreview')}</button>
                  ) : null}
                  <button className="ghostButton" onClick={closePdfPreview}>{t('pdf.close')}</button>
                </div>
              </div>
              {pdfRedactionMode && pdfPreview.pageImageUrl ? (
                <div className="pdfRedactionWorkspace">
                  <div
                    className="pdfRedactionCanvas"
                    ref={pdfRedactionCanvasRef}
                    onMouseDown={handlePdfRedactionPointerDown}
                    onMouseMove={handlePdfRedactionPointerMove}
                    onMouseUp={finishPdfRedactionPointer}
                    onMouseLeave={finishPdfRedactionPointer}
                  >
                    <img src={pdfPreview.pageImageUrl} alt={pdfPreview.title || t('pdf.pagePreview')} className="pdfRedactionImage" onLoad={handlePdfRedactionImageLoad} draggable={false} onDragStart={(event) => event.preventDefault()} />
                    <div
                      className="pdfRedactionBoxLayer"
                      style={pdfRedactionRenderRect
                        ? { left: pdfRedactionRenderRect.left, top: pdfRedactionRenderRect.top, width: pdfRedactionRenderRect.width, height: pdfRedactionRenderRect.height }
                        : { left: 0, top: 0, width: '100%', height: '100%' }}
                    >
                      {(pdfRedactionSuggestions?.applied_rules || []).map((rule) => (
                        <div
                          key={`applied-${rule.id}`}
                          className="pdfRedactionBox appliedRedactionBox"
                          style={{ left: `${rule.x * 100}%`, top: `${rule.y * 100}%`, width: `${rule.w * 100}%`, height: `${rule.h * 100}%` }}
                        >
                          <span>{t('pdf.auto', { label: rule.label })}</span>
                        </div>
                      ))}
                      {(pdfRedactionSuggestions?.learning_rules || []).map((rule) => (
                        <div
                          key={`learning-${rule.id}`}
                          className="pdfRedactionBox learningRedactionBox"
                          style={{ left: `${rule.x * 100}%`, top: `${rule.y * 100}%`, width: `${rule.w * 100}%`, height: `${rule.h * 100}%` }}
                        >
                          <span>{t('pdf.learning', { label: rule.label, count: rule.usage_count })}</span>
                        </div>
                      ))}
                      {pdfRedactionDrafts.map((draft) => (
                        <div
                          key={`draft-box-${draft.id}`}
                          className="pdfRedactionBox draftRedactionBox"
                          style={{ left: `${draft.x * 100}%`, top: `${draft.y * 100}%`, width: `${draft.w * 100}%`, height: `${draft.h * 100}%` }}
                        >
                          <span>{t('pdf.draft', { label: draft.label })}</span>
                        </div>
                      ))}
                      {pdfRedactionDrawingBox ? (
                        <div
                          className="pdfRedactionBox activeRedactionBox"
                          style={{ left: `${pdfRedactionDrawingBox.x * 100}%`, top: `${pdfRedactionDrawingBox.y * 100}%`, width: `${pdfRedactionDrawingBox.w * 100}%`, height: `${pdfRedactionDrawingBox.h * 100}%` }}
                        >
                          <span>{pdfRedactionDrawingBox.label || pdfRedactionLabel}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <aside className="pdfRedactionSidebar">
                    <div className="pdfRedactionSection">
                      <span className="panelKicker">Redaction Learning</span>
                      <h3>{pdfRedactionSuggestions?.profile_label || t('pdf.redactionLearning')}</h3>
                      <p>{t('pdf.autoApplyHint', { threshold: pdfRedactionSuggestions?.auto_apply_threshold || 0 })}</p>
                    </div>
                    <div className="pdfRedactionSection">
                      <label className="fieldLabel" htmlFor="pdf-redaction-label">{t('pdf.regionLabel')}</label>
                      <input id="pdf-redaction-label" value={pdfRedactionLabel} onChange={(event) => setPdfRedactionLabel(event.target.value)} placeholder={t('pdf.regionLabelPlaceholder')} />
                      <p>{t('pdf.dragHint')}</p>
                    </div>
                    <div className="pdfRedactionSection">
                      <h4>{t('pdf.pendingBoxes')}</h4>
                      {pdfRedactionDrafts.length ? (
                        <div className="pdfRedactionDraftList">
                          {pdfRedactionDrafts.map((draft) => (
                            <div key={draft.id} className="pdfRedactionDraftCard">
                              <div>
                                <strong>{draft.label}</strong>
                                <span>{`P${draft.page} · x ${draft.x.toFixed(3)} / y ${draft.y.toFixed(3)} / w ${draft.w.toFixed(3)} / h ${draft.h.toFixed(3)}`}</span>
                              </div>
                              <div className="pdfRedactionDraftActions">
                                <button className="ghostButton compactActionButton" onClick={() => savePdfRedactionDraft(draft)} disabled={pdfRedactionSavingKey === String(draft.id)}>{pdfRedactionSavingKey === String(draft.id) ? t('pdf.saving') : t('pdf.save')}</button>
                                <button className="ghostButton compactActionButton" onClick={() => setPdfRedactionDrafts((current) => current.filter((item) => item.id !== draft.id))}>{t('pdf.remove')}</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : <div className="emptyState compactEmptyState">{t('pdf.noPendingBoxes')}</div>}
                    </div>
                    <div className="pdfRedactionSection">
                      <h4>{t('pdf.savedRules')}</h4>
                      {[...(pdfRedactionSuggestions?.applied_rules || []), ...(pdfRedactionSuggestions?.learning_rules || [])]
                        .filter((rule) => Number(rule.page) === Number(pdfPreview.page || 1))
                        .length ? (
                        <div className="pdfRedactionDraftList">
                          {[...(pdfRedactionSuggestions?.applied_rules || []), ...(pdfRedactionSuggestions?.learning_rules || [])]
                            .filter((rule) => Number(rule.page) === Number(pdfPreview.page || 1))
                            .map((rule) => (
                              <div key={`saved-${rule.id}`} className="pdfRedactionDraftCard">
                                <div>
                                  <strong>{rule.label}</strong>
                                  <span>{t('pdf.ruleMeta', { page: rule.page, x: rule.x.toFixed(3), y: rule.y.toFixed(3), w: rule.w.toFixed(3), h: rule.h.toFixed(3), count: rule.usage_count })}</span>
                                </div>
                                <div className="pdfRedactionDraftActions">
                                  <button className="ghostButton compactActionButton" onClick={() => deleteSavedRedactionRule(rule)} disabled={pdfRedactionSavingKey === `saved-${rule.id}`}>{pdfRedactionSavingKey === `saved-${rule.id}` ? t('pdf.deleting') : t('pdf.delete')}</button>
                                </div>
                              </div>
                            ))}
                        </div>
                      ) : <div className="emptyState compactEmptyState">{t('pdf.noSavedRules')}</div>}
                    </div>
                  </aside>
                </div>
              ) : pdfRedactionPreviewOnly && pdfPreview.pageImageUrl ? (
                <div className="pdfRedactionPreviewOverlay">
                  <div className="pdfRedactionCanvas pdfRedactionReadOnlyCanvas" ref={pdfRedactionCanvasRef} onMouseDown={handlePdfDragScrollDown} onMouseMove={handlePdfDragScrollMove} onMouseUp={handlePdfDragScrollUp} onMouseLeave={handlePdfDragScrollLeave}>
                    <div className="pdfPreviewScaleWrapper" style={{ '--pdf-preview-scale': pdfPreviewScale }}>
                    <img src={pdfPreview.pageImageUrl} alt={pdfPreview.title || t('pdf.pagePreview')} className="pdfRedactionImage" onLoad={handlePdfRedactionImageLoad} draggable={false} onDragStart={(event) => event.preventDefault()} />
                    <div
                      className="pdfRedactionBoxLayer"
                      style={pdfRedactionRenderRect
                        ? { left: pdfRedactionRenderRect.left, top: pdfRedactionRenderRect.top, width: pdfRedactionRenderRect.width, height: pdfRedactionRenderRect.height }
                        : { left: 0, top: 0, width: '100%', height: '100%' }}
                    >
                      {(pdfRedactionSuggestions?.applied_rules || []).map((rule) => (
                        <div
                          key={`preview-applied-${rule.id}`}
                          className="pdfRedactionBox appliedRedactionBox"
                          style={{ left: `${rule.x * 100}%`, top: `${rule.y * 100}%`, width: `${rule.w * 100}%`, height: `${rule.h * 100}%` }}
                        >
                          <span>{t('pdf.auto', { label: rule.label })}</span>
                        </div>
                      ))}
                      {(pdfRedactionSuggestions?.learning_rules || []).map((rule) => (
                        <div
                          key={`preview-learning-${rule.id}`}
                          className="pdfRedactionBox learningRedactionBox"
                          style={{ left: `${rule.x * 100}%`, top: `${rule.y * 100}%`, width: `${rule.w * 100}%`, height: `${rule.h * 100}%` }}
                        >
                          <span>{t('pdf.learning', { label: rule.label, count: rule.usage_count })}</span>
                        </div>
                      ))}
                    </div>
                    </div>
                  </div>
                  <div className="pdfPreviewPageNav">
                    <button className="ghostButton" onClick={() => changePreviewPage(-1)} disabled={Number(pdfPreview.page || 1) <= 1}>{t('workspace.prevPage')}</button>
                    <span>{t('pdf.page', { page: pdfPreview.page || 1 })}</span>
                    <button className="ghostButton" onClick={() => changePreviewPage(1)}>{t('workspace.nextPage')}</button>
                  </div>
                </div>
              ) : (
                <iframe title={pdfPreview.title || t('pdf.preview')} src={pdfPreview.url} className="pdfModalFrame" />
              )}
            </div>
          </div>
        ) : null}

        {interestDetailDrawerOpen ? (
          <div className="drawerOverlay" onClick={() => setInterestDetailDrawerOpen(false)}>
            <aside className="drawerPanel" onClick={(event) => event.stopPropagation()}>
              <div className="drawerHeader">
                <div className="drawerHeaderMeta">
                  <span className="panelKicker">Financing Interest Details</span>
                  <h2>{t('interest.title')}</h2>
                  <p>{workspacePositionFilter === 'ALL' ? t('interest.hintAll', { count: financingInterestDetails.length }) : t('interest.hintScoped', { matched: workspaceInterestMetrics.matchedCount, unassigned: workspaceInterestMetrics.unassignedCount })}</p>
                </div>
                <button className="ghostButton" onClick={() => setInterestDetailDrawerOpen(false)}>{t('pdf.close')}</button>
              </div>
              <div className="tableWrap darkTableWrap drawerTableWrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t('panel.linked.businessDate')}</th>
                      <th>{t('interest.financingNo')}</th>
                      <th>{t('interest.stage')}</th>
                      <th>{t('interest.interest')}</th>
                      <th>{t('interest.linkedAsset')}</th>
                      <th>{t('panel.linked.pdf')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workspaceInterestMetrics.displayDetails.length ? workspaceInterestMetrics.displayDetails.map((item, index) => (
                      <tr key={`${item.reference_no || 'interest'}-${index}`}>
                        <td>{item.event_date || '-'}</td>
                        <td>{item.reference_no || '-'}</td>
                        <td>{item.event_stage || '-'}</td>
                        <td className="negative">{`${item.currency || 'USD'} ${formatNumber(item.interest_amount, 2)}`}</td>
                        <td>{item.linked_security_name || item.linked_asset_no ? `${item.linked_security_name || item.linked_asset_no}${item.linked_asset_no && item.linked_security_name ? ` (${item.linked_asset_no})` : item.linked_asset_no ? ` (${item.linked_asset_no})` : ''}` : t('interest.unassigned')}</td>
                        <td>{item.source_document_id ? <button className="ghostButton compactActionButton" onClick={() => previewStoredDocument(item.source_document_id, item.source_file || t('panel.linked.viewPdf'), item.source_page || null)}>{t('panel.linked.view')}{item.source_page ? ` P${item.source_page}` : ''}</button> : '-'}</td>
                      </tr>
                    )) : <tr><td colSpan="6" className="emptyTable">{t('interest.empty')}</td></tr>}
                  </tbody>
                </table>
              </div>
            </aside>
          </div>
        ) : null}
      </div>

      <nav className="mobileBottomNav mobileOnly">
        <button className={activeView === 'overview' ? 'mobileBottomNavItem activeMobileBottomNavItem' : 'mobileBottomNavItem'} onClick={() => setActiveView('overview')}>
          <ChartPie weight="duotone" />
          <span>{t('nav.mobile.overview')}</span>
        </button>
        <button className={activeView === 'workspace' ? 'mobileBottomNavItem activeMobileBottomNavItem' : 'mobileBottomNavItem'} onClick={() => setActiveView('workspace')}>
          <Briefcase weight="duotone" />
          <span>{t('nav.mobile.workspace')}</span>
        </button>
        <button className={activeView === 'analytics' ? 'mobileBottomNavItem activeMobileBottomNavItem' : 'mobileBottomNavItem'} onClick={() => setActiveView('analytics')}>
          <ChartLineUp weight="duotone" />
          <span>{t('nav.mobile.analytics')}</span>
        </button>
        {featureSettings.talker_enabled ? <button className={activeView === 'talker' ? 'mobileBottomNavItem activeMobileBottomNavItem' : 'mobileBottomNavItem'} onClick={() => setActiveView('talker')}>
          <ChatCircle weight="duotone" />
          <span>{t('nav.mobile.talker')}</span>
        </button> : null}
      </nav>
    </div>
  );
}

export default App;
